import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Shield,
  Building2,
  Server,
  Network,
  Lock,
  Users,
  FileText,
  Download,
  CheckCircle,
  Clock,
  AlertTriangle,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Loader2,
  ArrowLeft,
  Activity,
  Database,
  Cloud,
  Monitor,
  Cpu,
  HardDrive,
  Globe,
  ClipboardList,
  FileCheck,
  XCircle,
  MinusCircle,
  Search,
  FileDown,
  LayoutDashboard,
  AlertCircle,
  X,
  ListOrdered,
  ArrowUpDown,
  Image,
  Filter,
  Share2,
  Copy,
  Trash2,
  UserPlus,
  Mail,
  FileSpreadsheet
} from 'lucide-react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, RadialBarChart, RadialBar, Legend } from 'recharts';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Document, Packer, Paragraph, TextRun, HeadingLevel, Table, TableRow, TableCell, WidthType, BorderStyle, AlignmentType, ShadingType } from 'docx';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import SspDashboard from '../components/SspDashboard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Logo URLs
const ASPIRITECH_LOGO_URL = 'https://customer-assets.emergentagent.com/job_3658f96c-434f-413a-9767-d23e0efd2505/artifacts/cb0h7tri_aspiritech-logo.png';

// Toast notification component
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 5000);
    return () => clearTimeout(timer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const bgColor = type === 'error' ? 'bg-red-600' : type === 'success' ? 'bg-emerald-600' : 'bg-amber-600';
  
  return (
    <div className={`fixed top-20 right-4 z-50 ${bgColor} text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 max-w-md animate-slide-in`}>
      {type === 'error' ? <AlertCircle className="w-5 h-5 flex-shrink-0" /> : <CheckCircle className="w-5 h-5 flex-shrink-0" />}
      <span className="text-sm">{message}</span>
      <button onClick={onClose} className="ml-2 hover:opacity-80">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

const SspReport = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [sspData, setSSPData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [controlsBreakdown, setControlsBreakdown] = useState(null);
  const [detailedControls, setDetailedControls] = useState(null);
  const [nistControls, setNistControls] = useState(null);
  const [evidenceChecklist, setEvidenceChecklist] = useState(null);
  const [complianceScore, setComplianceScore] = useState(null);
  const [prioritizationData, setPrioritizationData] = useState(null);
  const [prioritizationSort, setPrioritizationSort] = useState({ field: 'sort', ascending: true });
  const [prioritizationFilters, setPrioritizationFilters] = useState({ family: 'all', status: 'all' });
  const [activeTab, setActiveTab] = useState('dashboard');
  const [generating, setGenerating] = useState(false);
  const [generatingFull, setGeneratingFull] = useState(false);
  const [generatingWord, setGeneratingWord] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterEffort, setFilterEffort] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [sortBy, setSortBy] = useState('default'); // default, effort, priority
  const [toast, setToast] = useState(null);
  const [dataLoadError, setDataLoadError] = useState(null);
  
  // Helper to show toast notifications
  const showToast = (message, type = 'error') => {
    setToast({ message, type });
  };
  
  // Export chart as JPG
  const exportChartAsJPG = (chartContainerId, filename) => {
    const chartContainer = document.querySelector(`[data-chart-id="${chartContainerId}"]`);
    if (!chartContainer) {
      showToast('Chart not found', 'error');
      return;
    }
    
    // Find the recharts wrapper inside the container
    const svgElement = chartContainer.querySelector('.recharts-wrapper svg');
    if (!svgElement) {
      showToast('Chart SVG not found', 'error');
      return;
    }
    
    // Create a canvas to draw the SVG
    const canvas = document.createElement('canvas');
    const svgRect = svgElement.getBoundingClientRect();
    const scale = 2; // Higher resolution
    canvas.width = svgRect.width * scale;
    canvas.height = svgRect.height * scale;
    const ctx = canvas.getContext('2d');
    
    // Fill background
    ctx.fillStyle = '#1E293B'; // Dark slate background
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.scale(scale, scale);
    
    // Convert SVG to data URL
    const svgData = new XMLSerializer().serializeToString(svgElement);
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);
    
    const img = new window.Image();
    img.onload = () => {
      ctx.drawImage(img, 0, 0);
      URL.revokeObjectURL(url);
      
      // Convert canvas to JPG and download
      canvas.toBlob((blob) => {
        const downloadUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = `${filename}.jpg`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(downloadUrl);
        showToast(`Chart exported as ${filename}.jpg`, 'success');
      }, 'image/jpeg', 0.95);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      showToast('Failed to export chart', 'error');
    };
    img.src = url;
  };
  
  // Export Prioritization data as Excel
  const exportPrioritizationAsExcel = () => {
    if (!prioritizationData?.data || prioritizationData.data.length === 0) {
      showToast('No prioritization data to export', 'error');
      return;
    }
    
    try {
      // Prepare data for export
      const exportData = prioritizationData.data.map((item, idx) => ({
        'Order': item.sort || idx + 1,
        'Control': item.secCntrl || '',
        'Description': item.definition || '',
        'Status': item.yesNo || '',
        'Priority': item.priority || '',
        'Control Family': item.secCntrl ? item.secCntrl.replace(/ #\s*\d+$/, '').trim() : ''
      }));
      
      // Create workbook
      const wb = XLSX.utils.book_new();
      
      // Summary sheet
      const summaryData = [
        ['Security Control Prioritization Report'],
        [''],
        ['Keystone Alliance HIPAA Compliance Assessment'],
        ['NIST SP 800-66 Framework'],
        [''],
        ['Summary Statistics'],
        ['Total Controls', prioritizationData.summary?.total || 0],
        ['Compliant (Yes)', prioritizationData.summary?.yesCount || 0],
        ['Non-Compliant (No)', prioritizationData.summary?.noCount || 0],
        ['Compliance Rate', `${prioritizationData.summary?.complianceRate || 0}%`],
        [''],
        ['Generated', new Date().toLocaleString()]
      ];
      const summaryWs = XLSX.utils.aoa_to_sheet(summaryData);
      
      // Style summary sheet column widths
      summaryWs['!cols'] = [{ wch: 25 }, { wch: 15 }];
      XLSX.utils.book_append_sheet(wb, summaryWs, 'Summary');
      
      // Controls sheet with all data
      const controlsWs = XLSX.utils.json_to_sheet(exportData);
      
      // Set column widths for better readability
      controlsWs['!cols'] = [
        { wch: 8 },   // Order
        { wch: 30 },  // Control
        { wch: 80 },  // Description
        { wch: 10 },  // Status
        { wch: 10 },  // Priority
        { wch: 35 }   // Control Family
      ];
      XLSX.utils.book_append_sheet(wb, controlsWs, 'All Controls');
      
      // Compliant controls sheet
      const compliantData = exportData.filter(item => item.Status === 'Yes');
      if (compliantData.length > 0) {
        const compliantWs = XLSX.utils.json_to_sheet(compliantData);
        compliantWs['!cols'] = controlsWs['!cols'];
        XLSX.utils.book_append_sheet(wb, compliantWs, 'Compliant');
      }
      
      // Non-compliant controls sheet
      const nonCompliantData = exportData.filter(item => item.Status === 'No');
      if (nonCompliantData.length > 0) {
        const nonCompliantWs = XLSX.utils.json_to_sheet(nonCompliantData);
        nonCompliantWs['!cols'] = controlsWs['!cols'];
        XLSX.utils.book_append_sheet(wb, nonCompliantWs, 'Non-Compliant');
      }
      
      // Generate filename with date
      const date = new Date().toISOString().split('T')[0];
      const filename = `Keystone_Prioritization_Report_${date}.xlsx`;
      
      // Export
      XLSX.writeFile(wb, filename);
      showToast(`Exported to ${filename}`, 'success');
    } catch (error) {
      console.error('Excel export error:', error);
      showToast('Failed to export Excel file', 'error');
    }
  };
  
  // Get assessment ID from URL query params
  const searchParams = new URLSearchParams(window.location.search);
  const assessmentId = searchParams.get('assessmentId');

  useEffect(() => {
    fetchSSPData();
  }, [assessmentId]);

  const fetchSSPData = async () => {
    try {
      setDataLoadError(null);
      // Build query string for assessment-specific data
      const queryParam = assessmentId ? `?assessment_id=${assessmentId}` : '';
      
      const endpoints = [
        { name: 'sspData', url: `${API_URL}/api/ssp-report/keystone${queryParam}` },
        { name: 'summary', url: `${API_URL}/api/ssp-report/keystone/summary${queryParam}` },
        { name: 'controlsBreakdown', url: `${API_URL}/api/ssp-report/keystone/controls-breakdown${queryParam}` },
        { name: 'detailedControls', url: `${API_URL}/api/ssp-report/keystone/detailed-controls${queryParam}` },
        { name: 'nistControls', url: `${API_URL}/api/ssp-report/keystone/nist-controls${queryParam}` },
        { name: 'evidenceChecklist', url: `${API_URL}/api/ssp-report/keystone/evidence-checklist${queryParam}` },
        { name: 'complianceScore', url: `${API_URL}/api/ssp-report/keystone/compliance-score${queryParam}` },
        { name: 'prioritization', url: `${API_URL}/api/ssp-report/keystone/prioritization` }
      ];
      
      const results = await Promise.allSettled(
        endpoints.map(async (ep) => {
          const res = await fetch(ep.url);
          if (!res.ok) {
            throw new Error(`${ep.name}: HTTP ${res.status}`);
          }
          return { name: ep.name, data: await res.json() };
        })
      );
      
      const errors = [];
      results.forEach((result, idx) => {
        if (result.status === 'fulfilled') {
          const { name, data } = result.value;
          switch (name) {
            case 'sspData': setSSPData(data); break;
            case 'summary': setSummary(data); break;
            case 'controlsBreakdown': setControlsBreakdown(data); break;
            case 'detailedControls': setDetailedControls(data); break;
            case 'nistControls': setNistControls(data); break;
            case 'evidenceChecklist': setEvidenceChecklist(data); break;
            case 'complianceScore': setComplianceScore(data); break;
            case 'prioritization': setPrioritizationData(data); break;
          }
        } else {
          errors.push(endpoints[idx].name);
          console.error(`Failed to load ${endpoints[idx].name}:`, result.reason);
        }
      });
      
      if (errors.length > 0) {
        setDataLoadError(`Some data failed to load: ${errors.join(', ')}`);
      }
    } catch (error) {
      console.error('Failed to fetch SSP data:', error);
      setDataLoadError('Failed to load report data. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  const toggleCategory = (categoryName) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryName]: !prev[categoryName]
    }));
  };

  const getAnswerIcon = (answer) => {
    switch (answer) {
      case 'Y': return <CheckCircle className="w-5 h-5 text-emerald-400" />;
      case 'N': return <XCircle className="w-5 h-5 text-red-400" />;
      case 'NA': return <MinusCircle className="w-5 h-5 text-slate-400" />;
      case 'P': return <Clock className="w-5 h-5 text-amber-400" />;
      default: return <MinusCircle className="w-5 h-5 text-slate-500" />;
    }
  };

  const getAnswerLabel = (answer) => {
    switch (answer) {
      case 'Y': return 'Yes';
      case 'N': return 'No';
      case 'NA': return 'N/A';
      case 'P': return 'Partial';
      default: return 'No Response';
    }
  };

  const getAnswerBg = (answer) => {
    switch (answer) {
      case 'Y': return 'bg-emerald-500/20 border-emerald-500/30 text-emerald-400';
      case 'N': return 'bg-red-500/20 border-red-500/30 text-red-400';
      case 'NA': return 'bg-slate-500/20 border-slate-500/30 text-slate-400';
      case 'P': return 'bg-amber-500/20 border-amber-500/30 text-amber-400';
      default: return 'bg-slate-700/50 border-slate-600 text-slate-500';
    }
  };

  const getStatusColor = (status) => {
    const s = status?.toLowerCase() || '';
    if (s.includes('implemented') && !s.includes('partially')) return 'text-emerald-400';
    if (s.includes('partially')) return 'text-amber-400';
    if (s.includes('planned')) return 'text-blue-400';
    return 'text-slate-400';
  };

  const getStatusBg = (status) => {
    const s = status?.toLowerCase() || '';
    if (s.includes('implemented') && !s.includes('partially')) return 'bg-emerald-500/20 border-emerald-500/30';
    if (s.includes('partially')) return 'bg-amber-500/20 border-amber-500/30';
    if (s.includes('planned')) return 'bg-blue-500/20 border-blue-500/30';
    return 'bg-slate-500/20 border-slate-500/30';
  };

  // Chart data - Compliant vs Planned (Planned = 158 - Compliant)
  const compliantCount = summary?.controls?.implemented || 0;
  const plannedCount = 158 - compliantCount;
  
  const controlsChartData = summary ? [
    { name: 'Compliant', value: compliantCount, color: '#10b981' },
    { name: 'Planned', value: plannedCount, color: '#3b82f6' }
  ] : [];

  const progressData = summary ? [
    { name: 'Implementation', value: summary.implementation_rate, fill: '#8b5cf6' }
  ] : [];

  const generatePDF = async () => {
    // Validate required data before generating
    if (!summary || !complianceScore) {
      showToast('Unable to generate PDF: Report data not fully loaded. Please refresh the page and try again.', 'error');
      return;
    }
    
    setGenerating(true);
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      
      // ==================== COVER PAGE ====================
      // Dark gradient background
      doc.setFillColor(15, 23, 42);
      doc.rect(0, 0, pageWidth, pageHeight, 'F');
      
      // Top accent bar - gradient effect
      doc.setFillColor(139, 92, 246);
      doc.rect(0, 0, pageWidth, 6, 'F');
      doc.setFillColor(59, 130, 246);
      doc.rect(0, 6, pageWidth, 2, 'F');
      
      // COLT branding (top left)
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.text('Colt Cybersecurity Consultants', 20, 30);
      
      // Aspiritech branding (top right)
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(16);
      doc.setFont(undefined, 'bold');
      doc.text('Aspiritech', pageWidth - 50, 30);
      doc.setFontSize(8);
      doc.setTextColor(251, 191, 36);
      doc.text('Cybersecurity', pageWidth - 50, 38);
      
      // Main title
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(36);
      doc.setFont(undefined, 'bold');
      doc.text('System Security Plan', pageWidth / 2, 80, { align: 'center' });
      
      // Organization name
      doc.setFontSize(24);
      doc.setTextColor(139, 92, 246);
      doc.text(sspData?.organization?.name || 'Keystone Alliance', pageWidth / 2, 100, { align: 'center' });
      
      // System name
      doc.setFontSize(14);
      doc.setTextColor(148, 163, 184);
      doc.text(sspData?.organization?.system_name || 'KA-IS', pageWidth / 2, 115, { align: 'center' });
      
      // Decorative box
      doc.setFillColor(30, 41, 59);
      doc.roundedRect(30, 135, pageWidth - 60, 60, 5, 5, 'F');
      doc.setDrawColor(139, 92, 246);
      doc.setLineWidth(0.5);
      doc.roundedRect(30, 135, pageWidth - 60, 60, 5, 5, 'S');
      
      // Key stats in box
      doc.setFontSize(11);
      doc.setTextColor(255, 255, 255);
      doc.text('Compliance Framework:', 45, 152);
      doc.setTextColor(139, 92, 246);
      doc.text('NIST SP 800-66 / HIPAA', 110, 152);
      
      doc.setTextColor(255, 255, 255);
      doc.text('Implementation Rate:', 45, 165);
      doc.setTextColor(16, 185, 129);
      doc.text(`${summary?.implementation_rate || 0}%`, 100, 165);
      
      doc.setTextColor(255, 255, 255);
      doc.text('Total Controls:', 45, 178);
      doc.setTextColor(59, 130, 246);
      doc.text(`${summary?.controls?.total || 0} controls`, 88, 178);
      
      // Document info
      doc.setFontSize(12);
      doc.setTextColor(148, 163, 184);
      doc.text(`Version ${sspData?.organization?.version || '1.0'}`, pageWidth / 2, 220, { align: 'center' });
      doc.text(sspData?.organization?.date || 'February 2026', pageWidth / 2, 232, { align: 'center' });
      
      // Tagline
      doc.setFontSize(14);
      doc.setTextColor(251, 191, 36);
      doc.text('Masks Down. Shields Up.', pageWidth / 2, 255, { align: 'center' });
      
      // Footer
      doc.setFontSize(9);
      doc.setTextColor(100, 116, 139);
      doc.text('Prepared by Colt Cybersecurity Consultants in partnership with Aspiritech', pageWidth / 2, pageHeight - 20, { align: 'center' });
      
      // ==================== TABLE OF CONTENTS ====================
      doc.addPage();
      addHeader(doc, pageWidth, 'Table of Contents');
      
      let y = 55;
      const tocItems = [
        { num: '1', title: 'Executive Summary', page: '3' },
        { num: '2', title: 'System Identification', page: '4' },
        { num: '3', title: 'Operational Status', page: '5' },
        { num: '4', title: 'System Environment', page: '6' },
        { num: '5', title: 'Security Controls Overview', page: '7' },
        { num: '6', title: 'Data Flow & Interconnections', page: '8' },
        { num: '7', title: 'Compliance Metrics', page: '9' },
        { num: '8', title: 'Priority Action Items', page: '10' },
      ];
      
      doc.setFontSize(12);
      tocItems.forEach(item => {
        doc.setFont(undefined, 'bold');
        doc.setTextColor(139, 92, 246);
        doc.text(item.num + '.', 25, y);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(30, 41, 59);
        doc.text(item.title, 35, y);
        
        // Dotted line
        doc.setTextColor(200, 200, 200);
        const dotLine = '.'.repeat(80);
        doc.text(dotLine, 90, y);
        
        doc.setTextColor(30, 41, 59);
        doc.text(item.page, pageWidth - 25, y);
        y += 14;
      });
      
      // ==================== EXECUTIVE SUMMARY ====================
      doc.addPage();
      addHeader(doc, pageWidth, '1. Executive Summary');
      
      y = 55;
      doc.setFontSize(11);
      doc.setTextColor(30, 41, 59);
      
      const execSummary = sspData?.executive_summary;
      if (execSummary) {
        // Overview
        doc.setFont(undefined, 'bold');
        doc.setTextColor(139, 92, 246);
        doc.text('Overview', 20, y);
        y += 8;
        doc.setFont(undefined, 'normal');
        doc.setTextColor(50, 50, 50);
        const overviewLines = doc.splitTextToSize(execSummary.overview, pageWidth - 40);
        doc.text(overviewLines, 20, y);
        y += overviewLines.length * 5 + 10;
        
        // Data Types
        doc.setFont(undefined, 'bold');
        doc.setTextColor(139, 92, 246);
        doc.text('Data Classification', 20, y);
        y += 8;
        doc.setFont(undefined, 'normal');
        doc.setTextColor(50, 50, 50);
        const dataLines = doc.splitTextToSize(execSummary.data_types, pageWidth - 40);
        doc.text(dataLines, 20, y);
        y += dataLines.length * 5 + 10;
        
        // Core Systems Table
        doc.setFont(undefined, 'bold');
        doc.setTextColor(139, 92, 246);
        doc.text('Core Information Systems', 20, y);
        y += 5;
        
        autoTable(doc, {
          startY: y,
          head: [['System']],
          body: execSummary.core_systems.map(s => [s]),
          theme: 'striped',
          headStyles: { fillColor: [139, 92, 246], textColor: [255, 255, 255], fontSize: 9 },
          bodyStyles: { textColor: [30, 41, 59], fontSize: 9 },
          margin: { left: 20, right: 20 }
        });
        
        y = doc.lastAutoTable.finalY + 10;
        
        // Service Providers
        if (y < pageHeight - 80) {
          doc.setFont(undefined, 'bold');
          doc.setTextColor(139, 92, 246);
          doc.text('Key Service Providers', 20, y);
          y += 5;
          
          autoTable(doc, {
            startY: y,
            head: [['Provider', 'Service']],
            body: execSummary.service_providers.map(p => [p.name, p.service]),
            theme: 'striped',
            headStyles: { fillColor: [59, 130, 246], textColor: [255, 255, 255], fontSize: 9 },
            bodyStyles: { textColor: [30, 41, 59], fontSize: 9 },
            margin: { left: 20, right: 20 }
          });
        }
      }
      
      // ==================== SYSTEM IDENTIFICATION ====================
      doc.addPage();
      addHeader(doc, pageWidth, '2. System Identification');
      
      y = 55;
      const sysId = sspData?.system_identification;
      if (sysId) {
        // General Support Systems
        doc.setFont(undefined, 'bold');
        doc.setTextColor(139, 92, 246);
        doc.text('General Support Systems', 20, y);
        y += 5;
        
        autoTable(doc, {
          startY: y,
          body: sysId.system_type.general_support.map(s => [s]),
          theme: 'plain',
          bodyStyles: { textColor: [30, 41, 59], fontSize: 9 },
          margin: { left: 20, right: 20 }
        });
        
        y = doc.lastAutoTable.finalY + 10;
        
        // Major Applications
        doc.setFont(undefined, 'bold');
        doc.setTextColor(59, 130, 246);
        doc.text('Major Applications', 20, y);
        y += 5;
        
        autoTable(doc, {
          startY: y,
          body: sysId.system_type.major_applications.map(s => [s]),
          theme: 'plain',
          bodyStyles: { textColor: [30, 41, 59], fontSize: 9 },
          margin: { left: 20, right: 20 }
        });
        
        y = doc.lastAutoTable.finalY + 10;
        
        // Information Types
        doc.setFont(undefined, 'bold');
        doc.setTextColor(16, 185, 129);
        doc.text('Information Types', 20, y);
        y += 8;
        
        doc.setFont(undefined, 'normal');
        doc.setFontSize(10);
        doc.setTextColor(50, 50, 50);
        doc.text('Primary:', 25, y);
        const primaryLines = doc.splitTextToSize(sysId.info_type.primary, pageWidth - 60);
        doc.text(primaryLines, 50, y);
        y += primaryLines.length * 5 + 5;
        
        doc.text('Supporting:', 25, y);
        doc.text(sysId.info_type.supporting, 55, y);
        y += 15;
        
        // Ownership
        doc.setFont(undefined, 'bold');
        doc.setTextColor(139, 92, 246);
        doc.setFontSize(11);
        doc.text('System Ownership', 20, y);
        y += 5;
        
        autoTable(doc, {
          startY: y,
          head: [['Role', 'Department']],
          body: [
            [sysId.owner.role, sysId.owner.department],
            [sysId.management.role, sysId.management.department]
          ],
          theme: 'striped',
          headStyles: { fillColor: [139, 92, 246], textColor: [255, 255, 255], fontSize: 9 },
          bodyStyles: { textColor: [30, 41, 59], fontSize: 9 },
          margin: { left: 20, right: 20 }
        });
      }
      
      // ==================== OPERATIONAL STATUS ====================
      doc.addPage();
      addHeader(doc, pageWidth, '3. Operational Status');
      
      y = 55;
      const opStatus = sspData?.operational_status;
      if (opStatus) {
        // Operational Items
        doc.setFillColor(16, 185, 129, 30);
        doc.roundedRect(18, y - 5, pageWidth - 36, 8, 2, 2, 'F');
        doc.setFont(undefined, 'bold');
        doc.setTextColor(16, 185, 129);
        doc.text('Operational Systems', 20, y);
        y += 8;
        
        opStatus.operational.forEach(item => {
          doc.setFont(undefined, 'normal');
          doc.setTextColor(50, 50, 50);
          doc.setFontSize(9);
          doc.text('•  ' + item, 25, y);
          y += 6;
        });
        
        y += 8;
        
        // Under Development
        doc.setFillColor(59, 130, 246, 30);
        doc.roundedRect(18, y - 5, pageWidth - 36, 8, 2, 2, 'F');
        doc.setFont(undefined, 'bold');
        doc.setTextColor(59, 130, 246);
        doc.setFontSize(11);
        doc.text('Under Development', 20, y);
        y += 8;
        
        opStatus.under_development.forEach(item => {
          doc.setFont(undefined, 'normal');
          doc.setTextColor(50, 50, 50);
          doc.setFontSize(9);
          doc.text('•  ' + item, 25, y);
          y += 6;
          if (y > pageHeight - 30) {
            doc.addPage();
            addHeader(doc, pageWidth, '3. Operational Status (continued)');
            y = 55;
          }
        });
        
        y += 8;
        
        // Major Modifications
        if (y < pageHeight - 50) {
          doc.setFillColor(245, 158, 11, 30);
          doc.roundedRect(18, y - 5, pageWidth - 36, 8, 2, 2, 'F');
          doc.setFont(undefined, 'bold');
          doc.setTextColor(245, 158, 11);
          doc.setFontSize(11);
          doc.text('Major Modifications In Progress', 20, y);
          y += 8;
          
          opStatus.major_modifications.forEach(item => {
            doc.setFont(undefined, 'normal');
            doc.setTextColor(50, 50, 50);
            doc.setFontSize(9);
            doc.text('•  ' + item, 25, y);
            y += 6;
          });
        }
      }
      
      // ==================== SYSTEM ENVIRONMENT ====================
      doc.addPage();
      addHeader(doc, pageWidth, '4. System Environment');
      
      y = 55;
      const sysEnv = sspData?.system_environment;
      if (sysEnv) {
        // Physical Locations
        doc.setFont(undefined, 'bold');
        doc.setTextColor(139, 92, 246);
        doc.text('Physical Locations', 20, y);
        y += 5;
        
        const locData = sysEnv.physical_locations.map(loc => [
          loc.name,
          loc.features.join(', ')
        ]);
        
        autoTable(doc, {
          startY: y,
          head: [['Location', 'Features']],
          body: locData,
          theme: 'striped',
          headStyles: { fillColor: [139, 92, 246], textColor: [255, 255, 255], fontSize: 9 },
          bodyStyles: { textColor: [30, 41, 59], fontSize: 8 },
          columnStyles: { 0: { cellWidth: 45 }, 1: { cellWidth: 'auto' } },
          margin: { left: 20, right: 20 }
        });
        
        y = doc.lastAutoTable.finalY + 12;
        
        // Major Components
        const components = sysEnv.major_components;
        
        doc.setFont(undefined, 'bold');
        doc.setTextColor(59, 130, 246);
        doc.text('Servers & Infrastructure', 20, y);
        y += 5;
        
        autoTable(doc, {
          startY: y,
          body: components.servers_infrastructure.map(s => [s]),
          theme: 'plain',
          bodyStyles: { textColor: [30, 41, 59], fontSize: 8 },
          margin: { left: 20, right: 20 }
        });
        
        y = doc.lastAutoTable.finalY + 8;
        
        if (y < pageHeight - 60) {
          doc.setFont(undefined, 'bold');
          doc.setTextColor(16, 185, 129);
          doc.text('Software & Security Stack', 20, y);
          y += 5;
          
          autoTable(doc, {
            startY: y,
            body: components.software_security.map(s => [s]),
            theme: 'plain',
            bodyStyles: { textColor: [30, 41, 59], fontSize: 8 },
            margin: { left: 20, right: 20 }
          });
        }
      }
      
      // ==================== SECURITY CONTROLS ====================
      doc.addPage();
      addHeader(doc, pageWidth, '5. Security Controls Overview');
      
      y = 55;
      const controls = sspData?.security_controls;
      if (controls && summary) {
        // Calculate compliant and planned based on new logic
        // Compliant = implemented controls, Planned = 158 - compliant
        const compliantControls = summary.controls?.implemented || 0;
        const plannedControls = 158 - compliantControls;
        
        // Controls Summary Stats - Only show Compliant and Planned
        doc.setFillColor(30, 41, 59);
        doc.roundedRect(20, y - 5, pageWidth - 40, 35, 3, 3, 'F');
        
        const statsX = 50;
        doc.setFontSize(20);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(16, 185, 129);
        doc.text(String(compliantControls), statsX, y + 12);
        doc.setFontSize(8);
        doc.setTextColor(148, 163, 184);
        doc.text('Compliant', statsX - 5, y + 20);
        
        doc.setFontSize(20);
        doc.setTextColor(59, 130, 246);
        doc.text(String(plannedControls), statsX + 80, y + 12);
        doc.setFontSize(8);
        doc.setTextColor(148, 163, 184);
        doc.text('Planned', statsX + 80, y + 20);
        
        y += 45;
        
        // Control Families as bullet points (not table)
        doc.setFont(undefined, 'bold');
        doc.setTextColor(139, 92, 246);
        doc.setFontSize(12);
        doc.text('Control Family Status:', 20, y);
        y += 10;
        
        const controlFamilies = [
          { name: 'Access Control', status: controls.access_control?.status || 'Planned' },
          { name: 'Awareness & Training', status: controls.awareness_training?.status || 'Planned' },
          { name: 'Audit & Accountability', status: controls.audit_accountability?.status || 'Planned' },
          { name: 'Configuration Management', status: controls.configuration_management?.status || 'Planned' },
          { name: 'Identification & Authentication', status: controls.identification_authentication?.status || 'Planned' },
          { name: 'Incident Response', status: controls.incident_response?.status || 'Planned' },
          { name: 'System & Communications Protection', status: 'Planned' },
          { name: 'System & Information Integrity', status: 'Planned' },
          { name: 'Physical Security', status: 'Planned' },
          { name: 'Risk Assessment', status: 'Planned' },
          { name: 'Continuity Planning', status: 'Planned' }
        ];
        
        // Replace "Partially Implemented" with "Planned" in status
        controlFamilies.forEach(family => {
          const status = family.status.toLowerCase().includes('partial') ? 'Planned' : family.status;
          const statusColor = status.toLowerCase().includes('implemented') && !status.toLowerCase().includes('partial') 
            ? [16, 185, 129]  // Green for Implemented
            : [59, 130, 246]; // Blue for Planned
          
          doc.setFont(undefined, 'normal');
          doc.setTextColor(50, 50, 50);
          doc.setFontSize(10);
          doc.text(`•  ${family.name}: `, 25, y);
          
          // Status in color
          const nameWidth = doc.getTextWidth(`•  ${family.name}: `);
          doc.setTextColor(...statusColor);
          doc.setFont(undefined, 'bold');
          doc.text(status.toLowerCase().includes('partial') ? 'Planned' : status, 25 + nameWidth, y);
          
          y += 7;
          if (y > pageHeight - 40) {
            doc.addPage();
            addHeader(doc, pageWidth, '5. Security Controls Overview (continued)');
            y = 55;
          }
        });
        
        y += 10;
        
        // ==================== COMPLIANCE CHARTS ====================
        // Add pie chart and bar graph at end of Section 5
        
        // Check if we need a new page for charts
        if (y > pageHeight - 120) {
          doc.addPage();
          addHeader(doc, pageWidth, '5. Security Controls Overview (continued)');
          y = 55;
        }
        
        // Section title for charts
        doc.setFont(undefined, 'bold');
        doc.setTextColor(139, 92, 246);
        doc.setFontSize(12);
        doc.text('Compliance Visualization', 20, y);
        y += 10;
        
        // --- PIE CHART: Compliant vs Planned ---
        const pieX = 55;
        const pieY = y + 35;
        const pieRadius = 25;
        
        // Calculate angles for pie chart
        const compliantAngle = (compliantControls / 158) * 360;
        const plannedAngle = (plannedControls / 158) * 360;
        
        // Draw compliant slice (green)
        doc.setFillColor(16, 185, 129);
        if (compliantControls > 0) {
          // Draw filled pie slice for compliant
          const startAngle = -90; // Start from top
          const endAngle = startAngle + compliantAngle;
          drawPieSlice(doc, pieX, pieY, pieRadius, startAngle, endAngle, [16, 185, 129]);
        }
        
        // Draw planned slice (blue)
        doc.setFillColor(59, 130, 246);
        if (plannedControls > 0) {
          const startAngle = -90 + compliantAngle;
          const endAngle = startAngle + plannedAngle;
          drawPieSlice(doc, pieX, pieY, pieRadius, startAngle, endAngle, [59, 130, 246]);
        }
        
        // Pie chart border
        doc.setDrawColor(100, 100, 100);
        doc.setLineWidth(0.5);
        doc.circle(pieX, pieY, pieRadius, 'S');
        
        // Pie chart legend
        doc.setFontSize(9);
        doc.setFont(undefined, 'normal');
        
        // Compliant legend
        doc.setFillColor(16, 185, 129);
        doc.rect(pieX + 35, pieY - 10, 8, 8, 'F');
        doc.setTextColor(50, 50, 50);
        doc.text(`Compliant: ${compliantControls}`, pieX + 46, pieY - 4);
        
        // Planned legend
        doc.setFillColor(59, 130, 246);
        doc.rect(pieX + 35, pieY + 2, 8, 8, 'F');
        doc.setTextColor(50, 50, 50);
        doc.text(`Planned: ${plannedControls}`, pieX + 46, pieY + 8);
        
        // Pie chart title
        doc.setFont(undefined, 'bold');
        doc.setTextColor(30, 41, 59);
        doc.setFontSize(10);
        doc.text('Controls Status', pieX - 15, y);
        
        // --- BAR GRAPH: Points by Domain ---
        const barStartX = pageWidth / 2 + 10;
        const barY = y;
        const barWidth = 60;
        const barHeight = 8;
        const maxPoints = 30; // Max points for scaling
        
        // Bar chart title
        doc.setFont(undefined, 'bold');
        doc.setTextColor(30, 41, 59);
        doc.setFontSize(10);
        doc.text('Points by Domain', barStartX, barY);
        
        // Domain scores (example data - would come from complianceScore in real implementation)
        const domainScores = complianceScore?.familyScores?.slice(0, 6) || [
          { name: 'Access Control', score: compliantControls > 20 ? 20 : compliantControls },
          { name: 'Audit & Acct', score: Math.min(8, compliantControls) },
          { name: 'Awareness', score: Math.min(6, compliantControls) },
          { name: 'Config Mgmt', score: Math.min(5, compliantControls) },
          { name: 'ID & Auth', score: Math.min(4, compliantControls) },
          { name: 'Incident Resp', score: Math.min(3, compliantControls) }
        ];
        
        let barYPos = barY + 12;
        domainScores.forEach((domain, idx) => {
          const domainName = typeof domain === 'object' ? 
            (domain.name || domain.family || `Domain ${idx + 1}`).substring(0, 12) : 
            `Domain ${idx + 1}`;
          const score = typeof domain === 'object' ? (domain.score || domain.compliant || 0) : 0;
          const barLength = (score / maxPoints) * barWidth;
          
          // Domain label
          doc.setFont(undefined, 'normal');
          doc.setTextColor(80, 80, 80);
          doc.setFontSize(7);
          doc.text(domainName, barStartX, barYPos + 5);
          
          // Background bar
          doc.setFillColor(230, 230, 230);
          doc.rect(barStartX + 35, barYPos, barWidth, barHeight, 'F');
          
          // Score bar
          doc.setFillColor(139, 92, 246);
          doc.rect(barStartX + 35, barYPos, barLength, barHeight, 'F');
          
          // Score label
          doc.setTextColor(50, 50, 50);
          doc.text(String(score), barStartX + 35 + barWidth + 3, barYPos + 6);
          
          barYPos += 12;
        });
        
        y = Math.max(pieY + pieRadius + 15, barYPos + 10);
      }
      
      // ==================== DATA FLOW ====================
      doc.addPage();
      addHeader(doc, pageWidth, '6. Data Flow & Interconnections');
      
      y = 55;
      const dataFlow = sspData?.data_flow;
      if (dataFlow) {
        // Data Flow Steps
        doc.setFont(undefined, 'bold');
        doc.setTextColor(139, 92, 246);
        doc.text('Data Flow Process', 20, y);
        y += 8;
        
        dataFlow.steps.forEach((step, idx) => {
          doc.setFillColor(139, 92, 246);
          doc.circle(28, y - 2, 3, 'F');
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(8);
          doc.text(String(idx + 1), 26, y);
          
          doc.setTextColor(50, 50, 50);
          doc.setFontSize(9);
          const stepLines = doc.splitTextToSize(step, pageWidth - 55);
          doc.text(stepLines, 38, y);
          y += stepLines.length * 5 + 8;
        });
        
        y += 10;
        
        // Interconnections Table
        doc.setFont(undefined, 'bold');
        doc.setTextColor(59, 130, 246);
        doc.setFontSize(11);
        doc.text('System Interconnections', 20, y);
        y += 5;
        
        const interconnectData = dataFlow.interconnections.map(i => [
          i.system,
          i.type,
          i.security
        ]);
        
        autoTable(doc, {
          startY: y,
          head: [['System/Entity', 'Type', 'Security Mechanism']],
          body: interconnectData,
          theme: 'striped',
          headStyles: { fillColor: [59, 130, 246], textColor: [255, 255, 255], fontSize: 9 },
          bodyStyles: { textColor: [30, 41, 59], fontSize: 8 },
          margin: { left: 20, right: 20 }
        });
      }
      
      // ==================== COMPLIANCE METRICS ====================
      doc.addPage();
      addHeader(doc, pageWidth, '7. Compliance Metrics');
      
      y = 55;
      if (summary) {
        // Implementation Rate Gauge
        doc.setFillColor(30, 41, 59);
        doc.roundedRect(20, y - 5, pageWidth - 40, 50, 5, 5, 'F');
        
        doc.setFontSize(14);
        doc.setTextColor(148, 163, 184);
        doc.text('Overall Implementation Rate', pageWidth / 2, y + 8, { align: 'center' });
        
        doc.setFontSize(36);
        doc.setFont(undefined, 'bold');
        const rate = summary.implementation_rate || 0;
        if (rate >= 70) {
          doc.setTextColor(16, 185, 129);
        } else if (rate >= 40) {
          doc.setTextColor(245, 158, 11);
        } else {
          doc.setTextColor(239, 68, 68);
        }
        doc.text(`${rate}%`, pageWidth / 2, y + 35, { align: 'center' });
        
        y += 60;
        
        // Key Metrics Grid
        const metricsData = [
          ['Core Systems', String(summary.core_systems_count || 0)],
          ['Service Providers', String(summary.service_providers_count || 0)],
          ['Operational Items', String(summary.operational_items || 0)],
          ['Items Under Development', String(summary.planned_items || 0)]
        ];
        
        autoTable(doc, {
          startY: y,
          head: [['Metric', 'Count']],
          body: metricsData,
          theme: 'striped',
          headStyles: { fillColor: [139, 92, 246], textColor: [255, 255, 255], fontSize: 10 },
          bodyStyles: { textColor: [30, 41, 59], fontSize: 10 },
          columnStyles: { 1: { halign: 'center', fontStyle: 'bold' } },
          margin: { left: 20, right: 20 }
        });
      }
      
      // ==================== PRIORITY ACTION ITEMS ====================
      doc.addPage();
      addHeader(doc, pageWidth, '8. Priority Action Items');
      
      y = 55;
      if (summary?.priority_areas) {
        doc.setFontSize(10);
        doc.setTextColor(50, 50, 50);
        doc.text('The following items require immediate attention to achieve NIST SP 800-66/HIPAA compliance:', 20, y);
        y += 12;
        
        const priorityData = summary.priority_areas.map(p => [
          p.area,
          p.priority,
          p.status
        ]);
        
        autoTable(doc, {
          startY: y,
          head: [['Action Item', 'Priority', 'Status']],
          body: priorityData,
          theme: 'striped',
          headStyles: { fillColor: [239, 68, 68], textColor: [255, 255, 255], fontSize: 10 },
          bodyStyles: { textColor: [30, 41, 59], fontSize: 9 },
          columnStyles: { 1: { halign: 'center' }, 2: { halign: 'center' } },
          margin: { left: 20, right: 20 },
          didParseCell: function(data) {
            if (data.column.index === 1 && data.section === 'body') {
              const priority = data.cell.text[0]?.toLowerCase() || '';
              if (priority === 'high') {
                data.cell.styles.textColor = [239, 68, 68];
                data.cell.styles.fontStyle = 'bold';
              } else if (priority === 'medium') {
                data.cell.styles.textColor = [245, 158, 11];
              }
            }
            if (data.column.index === 2 && data.section === 'body') {
              const status = data.cell.text[0]?.toLowerCase() || '';
              if (status.includes('progress')) {
                data.cell.styles.textColor = [59, 130, 246];
              }
            }
          }
        });
        
        y = doc.lastAutoTable.finalY + 20;
        
        // Recommendations box - dark background with light text for better readability
        doc.setFillColor(30, 41, 59); // Dark slate background
        doc.roundedRect(20, y, pageWidth - 40, 45, 3, 3, 'F');
        doc.setDrawColor(251, 191, 36); // Gold border
        doc.setLineWidth(1);
        doc.roundedRect(20, y, pageWidth - 40, 45, 3, 3, 'S');
        
        doc.setFont(undefined, 'bold');
        doc.setTextColor(251, 191, 36); // Gold title
        doc.setFontSize(11);
        doc.text('Recommendations', 30, y + 12);
        
        doc.setFont(undefined, 'normal');
        doc.setTextColor(255, 255, 255); // White text for recommendations
        doc.setFontSize(9);
        doc.text('1. Prioritize Access Control Policy formalization', 30, y + 24);
        doc.text('2. Implement centralized SIEM for comprehensive audit logging', 30, y + 33);
        doc.text('3. Complete MFA rollout across all privileged accounts', 30, y + 42);
      }
      
      // ==================== ADD FOOTERS ====================
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 2; i <= pageCount; i++) {
        doc.setPage(i);
        
        // Footer bar
        doc.setFillColor(248, 250, 252);
        doc.rect(0, pageHeight - 18, pageWidth, 18, 'F');
        doc.setFillColor(139, 92, 246);
        doc.rect(0, pageHeight - 18, pageWidth, 1, 'F');
        
        // Footer text
        doc.setFontSize(8);
        doc.setTextColor(100, 116, 139);
        doc.text('Colt Cybersecurity Consultants | Aspiritech', 20, pageHeight - 8);
        doc.text(`Keystone Alliance SSP v${sspData?.organization?.version || '1.0'}`, pageWidth / 2, pageHeight - 8, { align: 'center' });
        doc.text(`Page ${i} of ${pageCount}`, pageWidth - 25, pageHeight - 8);
      }
      
      // Save
      doc.save('Keystone_Alliance_SSP_Executive_Summary.pdf');
      showToast('Executive Summary PDF downloaded successfully!', 'success');
      
    } catch (error) {
      console.error('PDF generation failed:', error);
      showToast(`PDF generation failed: ${error.message || 'Unknown error'}. Please try again.`, 'error');
    } finally {
      setGenerating(false);
    }
  };

  // Generate Full Detailed Report PDF
  const generateFullPDF = async () => {
    // Validate required data before generating
    if (!summary || !complianceScore || !detailedControls) {
      showToast('Unable to generate PDF: Report data not fully loaded. Please refresh the page and try again.', 'error');
      return;
    }
    
    setGeneratingFull(true);
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      
      // ==================== COVER PAGE ====================
      doc.setFillColor(15, 23, 42);
      doc.rect(0, 0, pageWidth, pageHeight, 'F');
      
      // Top accent bar
      doc.setFillColor(139, 92, 246);
      doc.rect(0, 0, pageWidth, 6, 'F');
      doc.setFillColor(59, 130, 246);
      doc.rect(0, 6, pageWidth, 2, 'F');
      
      // COLT branding
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.text('Colt Cybersecurity Consultants', 20, 30);
      
      // Aspiritech branding
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(16);
      doc.setFont(undefined, 'bold');
      doc.text('Aspiritech', pageWidth - 50, 30);
      
      // Main title
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(32);
      doc.setFont(undefined, 'bold');
      doc.text('System Security Plan', pageWidth / 2, 70, { align: 'center' });
      doc.setFontSize(20);
      doc.text('Full Detailed Report', pageWidth / 2, 85, { align: 'center' });
      
      // Organization name
      doc.setFontSize(24);
      doc.setTextColor(139, 92, 246);
      doc.text(sspData?.organization?.name || 'Keystone Alliance', pageWidth / 2, 110, { align: 'center' });
      
      // Report contents box
      doc.setFillColor(30, 41, 59);
      doc.roundedRect(30, 130, pageWidth - 60, 80, 5, 5, 'F');
      doc.setDrawColor(139, 92, 246);
      doc.roundedRect(30, 130, pageWidth - 60, 80, 5, 5, 'S');
      
      doc.setFontSize(12);
      doc.setTextColor(255, 255, 255);
      doc.text('This report includes:', 45, 145);
      
      doc.setFontSize(10);
      doc.setTextColor(148, 163, 184);
      const contents = [
        `• ${detailedControls?.totalQuestions || 0} HIPAA Assessment Questions with Responses`,
        '• MUST Statements for each control',
        '• Evidence Requirements checklist',
        `• ${nistControls?.totalControls || 0} NIST SP 800-66 Controls`,
        '• HIPAA to NIST Control Mapping'
      ];
      contents.forEach((item, idx) => {
        doc.text(item, 50, 160 + (idx * 10));
      });
      
      // Document info
      doc.setFontSize(12);
      doc.setTextColor(148, 163, 184);
      doc.text(`Version ${sspData?.organization?.version || '1.0'}`, pageWidth / 2, 235, { align: 'center' });
      doc.text(sspData?.organization?.date || 'February 2026', pageWidth / 2, 247, { align: 'center' });
      
      // Tagline
      doc.setFontSize(14);
      doc.setTextColor(251, 191, 36);
      doc.text('Masks Down. Shields Up.', pageWidth / 2, 270, { align: 'center' });
      
      // ==================== TABLE OF CONTENTS ====================
      doc.addPage();
      addHeader(doc, pageWidth, 'Table of Contents');
      
      let y = 55;
      doc.setFontSize(11);
      const tocItems = [
        { title: 'HIPAA Compliance Score Dashboard', page: '3' },
        { title: 'HIPAA Assessment Summary', page: '4' },
        { title: 'Administrative Safeguards', page: '5' },
        { title: 'Technical Safeguards', page: '9' },
        { title: 'Physical Safeguards', page: '13' },
        { title: 'Organizational Requirements', page: '16' },
        { title: 'Policies and Documentation', page: '18' },
        { title: 'NIST SP 800-66 Controls', page: '20' },
        { title: 'Evidence Checklist', page: '26' }
      ];
      
      tocItems.forEach((item, idx) => {
        doc.setTextColor(139, 92, 246);
        doc.setFont(undefined, 'bold');
        doc.text(`${idx + 1}.`, 25, y);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(30, 41, 59);
        doc.text(item.title, 35, y);
        doc.setTextColor(100, 100, 100);
        doc.text(item.page, pageWidth - 25, y);
        y += 12;
      });
      
      // ==================== COMPLIANCE SCORE DASHBOARD ====================
      doc.addPage();
      addHeader(doc, pageWidth, 'HIPAA Compliance Score Dashboard');
      
      y = 55;
      
      if (complianceScore) {
        // Main Score Box
        doc.setFillColor(30, 41, 59);
        doc.roundedRect(20, y - 5, pageWidth - 40, 50, 5, 5, 'F');
        
        // Score percentage
        doc.setFontSize(36);
        doc.setFont(undefined, 'bold');
        const scoreColor = complianceScore.overallScore.percentage >= 70 ? [16, 185, 129] :
                          complianceScore.overallScore.percentage >= 40 ? [245, 158, 11] : [239, 68, 68];
        doc.setTextColor(...scoreColor);
        doc.text(`${complianceScore.overallScore.percentage}%`, 40, y + 22);
        
        // Score text
        doc.setFontSize(12);
        doc.setTextColor(148, 163, 184);
        doc.text('HIPAA Compliance Score', 40, y + 35);
        doc.text(complianceScore.overallScore.displayText, 40, y + 42);
        
        y += 60;
        
        // Summary Stats Row - 2x2 grid with LARGER boxes for better text fit
        const statBoxWidth = (pageWidth - 50) / 2;
        const statBoxHeight = 35; // Increased height for better text spacing
        const statsData = [
          { label: 'Total Controls', value: complianceScore.summary.totalControls, color: [139, 92, 246] },
          { label: 'Compliant', value: complianceScore.summary.compliant, color: [16, 185, 129] },
          { label: 'Non-Compliant', value: complianceScore.summary.nonCompliant, color: [239, 68, 68] },
          { label: 'Points Recoverable', value: complianceScore.summary.pointsRecoverable, color: [245, 158, 11] }
        ];
        
        statsData.forEach((stat, idx) => {
          const row = Math.floor(idx / 2);
          const col = idx % 2;
          const x = 20 + (col * statBoxWidth) + (col * 5);
          const yOffset = y + (row * (statBoxHeight + 8)); // Increased row spacing
          
          doc.setFillColor(241, 245, 249);
          doc.roundedRect(x, yOffset, statBoxWidth - 5, statBoxHeight, 4, 4, 'F');
          
          // Larger value text with better vertical centering
          doc.setFontSize(22);
          doc.setFont(undefined, 'bold');
          doc.setTextColor(...stat.color);
          doc.text(String(stat.value), x + 12, yOffset + 18);
          
          // Larger label text below value
          doc.setFontSize(11);
          doc.setFont(undefined, 'normal');
          doc.setTextColor(100, 116, 139);
          doc.text(stat.label, x + 12, yOffset + 28);
        });
        
        y += (statBoxHeight + 8) * 2 + 15; // Adjusted for new box heights
        
        // Control Family Breakdown Table
        doc.setFont(undefined, 'bold');
        doc.setTextColor(139, 92, 246);
        doc.setFontSize(12);
        doc.text('Control Family Score Breakdown', 20, y);
        y += 5;
        
        const tableData = complianceScore.familyScores.map(f => [
          f.name.length > 25 ? f.name.substring(0, 22) + '...' : f.name,
          `${f.score}/${f.maxScore}`,
          String(f.compliant),
          String(f.nonCompliant),
          String(f.applicableControls),
          `${f.percentComplete}%`
        ]);
        
        autoTable(doc, {
          startY: y,
          head: [['Control Family', 'Score', 'Compliant', 'Non-Comp', 'Total', '% Complete']],
          body: tableData,
          foot: [[
            'TOTAL',
            `${complianceScore.overallScore.score}/${complianceScore.overallScore.maxScore}`,
            String(complianceScore.summary.compliant),
            String(complianceScore.summary.nonCompliant),
            String(complianceScore.summary.applicableControls),
            `${complianceScore.overallScore.percentage}%`
          ]],
          theme: 'striped',
          headStyles: { fillColor: [139, 92, 246], textColor: [255, 255, 255], fontSize: 8, fontStyle: 'bold' },
          bodyStyles: { textColor: [30, 41, 59], fontSize: 8 },
          footStyles: { fillColor: [30, 41, 59], textColor: [255, 255, 255], fontSize: 8, fontStyle: 'bold' },
          columnStyles: {
            0: { cellWidth: 55 },
            1: { cellWidth: 22, halign: 'center' },
            2: { cellWidth: 22, halign: 'center' },
            3: { cellWidth: 22, halign: 'center' },
            4: { cellWidth: 18, halign: 'center' },
            5: { cellWidth: 25, halign: 'center' }
          },
          margin: { left: 20, right: 20 },
          didParseCell: function(data) {
            if (data.section === 'body') {
              if (data.column.index === 5) {
                const pct = parseFloat(data.cell.text[0]);
                if (pct >= 70) data.cell.styles.textColor = [16, 185, 129];
                else if (pct >= 40) data.cell.styles.textColor = [245, 158, 11];
                else data.cell.styles.textColor = [239, 68, 68];
              }
            }
          }
        });
        
        y = doc.lastAutoTable.finalY + 15;
        
        // Scoring methodology note
        doc.setFillColor(251, 191, 36, 30);
        doc.roundedRect(20, y, pageWidth - 40, 20, 3, 3, 'F');
        doc.setFont(undefined, 'bold');
        doc.setTextColor(180, 130, 0);
        doc.setFontSize(9);
        doc.text('Scoring Methodology:', 25, y + 8);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(100, 80, 0);
        doc.text('Each control = 1 point. Yes = 1pt, Partial = 0pt, No = 0pt, N/A = Excluded', 25, y + 15);
      }
      
      // ==================== HIPAA ASSESSMENT SUMMARY ====================
      doc.addPage();
      addHeader(doc, pageWidth, 'HIPAA Assessment Summary');
      
      y = 55;
      
      // Summary stats
      doc.setFillColor(30, 41, 59);
      doc.roundedRect(20, y - 5, pageWidth - 40, 30, 3, 3, 'F');
      
      const stats = detailedControls?.summary || {};
      const statsData = [
        { label: 'Yes', value: stats.yes || 0, color: [16, 185, 129] },
        { label: 'Partial', value: stats.partial || 0, color: [245, 158, 11] },
        { label: 'No', value: stats.no || 0, color: [239, 68, 68] },
        { label: 'N/A', value: stats.na || 0, color: [100, 116, 139] }
      ];
      
      let statX = 35;
      statsData.forEach(stat => {
        doc.setFontSize(18);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(...stat.color);
        doc.text(String(stat.value), statX, y + 10);
        doc.setFontSize(8);
        doc.setTextColor(148, 163, 184);
        doc.text(stat.label, statX, y + 18);
        statX += 40;
      });
      
      y += 40;
      
      // ==================== DETAILED HIPAA QUESTIONS - ONE QUESTION PER PAGE ====================
      detailedControls?.categories?.forEach((category, categoryIndex) => {
        category.controls.forEach((control, controlIndex) => {
          // Start each question on a new page
          doc.addPage();
          addHeader(doc, pageWidth, category.name);
          y = 55;
          
          // Question number indicator
          doc.setFontSize(9);
          doc.setTextColor(148, 163, 184);
          doc.text(`Question ${controlIndex + 1} of ${category.controls.length}`, pageWidth - 50, 38);
          
          // Get answer styling based on response (defaults to N/No from API)
          const answer = control.response?.answer || 'N';
          const answerConfig = {
            'Y': { label: 'YES', bgColor: [16, 185, 129], rowBgColor: [220, 252, 231], borderColor: [134, 239, 172] },
            'N': { label: 'NO', bgColor: [220, 38, 38], rowBgColor: [254, 242, 242], borderColor: [254, 202, 202] },
            'P': { label: 'PARTIAL', bgColor: [245, 158, 11], rowBgColor: [254, 243, 199], borderColor: [252, 211, 77] },
            'NA': { label: 'N/A', bgColor: [100, 116, 139], rowBgColor: [241, 245, 249], borderColor: [203, 213, 225] },
            'NR': { label: 'N/R', bgColor: [148, 163, 184], rowBgColor: [241, 245, 249], borderColor: [203, 213, 225] }
          };
          const config = answerConfig[answer] || answerConfig['N'];
          
          // LARGER Main question status box - more prominent
          const statusBoxWidth = 45;
          const statusBoxHeight = 25;
          doc.setFillColor(...config.bgColor);
          doc.roundedRect(20, y - 5, statusBoxWidth, statusBoxHeight, 4, 4, 'F');
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(14);
          doc.setFont(undefined, 'bold');
          const labelX = answer === 'NA' || answer === 'NR' ? 28 : (answer === 'PARTIAL' ? 24 : 32);
          doc.text(config.label, labelX, y + 8);
          
          // Question ID - larger and more visible
          doc.setTextColor(139, 92, 246);
          doc.setFontSize(12);
          doc.setFont(undefined, 'bold');
          doc.text(control.id, 75, y + 5);
          
          y += statusBoxHeight + 10;
          
          // Question text box - full width with better styling
          const questionBoxPadding = 10;
          doc.setFillColor(...config.rowBgColor);
          const questionLines = doc.splitTextToSize(control.text, pageWidth - 50);
          const questionBoxHeight = Math.max(30, questionLines.length * 7 + questionBoxPadding * 2);
          doc.roundedRect(20, y, pageWidth - 40, questionBoxHeight, 4, 4, 'F');
          doc.setDrawColor(...config.borderColor);
          doc.setLineWidth(0.5);
          doc.roundedRect(20, y, pageWidth - 40, questionBoxHeight, 4, 4, 'S');
          
          // Question text - larger font, dark text
          doc.setFont(undefined, 'normal');
          doc.setTextColor(30, 30, 40);
          doc.setFontSize(12);
          doc.text(questionLines, 25, y + questionBoxPadding + 5);
          
          y += questionBoxHeight + 15;
          
          // Response text - if available
          if (control.response?.responseText) {
            doc.setFillColor(248, 250, 252);
            const responseLines = doc.splitTextToSize(control.response.responseText, pageWidth - 60);
            const responseBoxHeight = responseLines.length * 6 + 15;
            doc.roundedRect(20, y, pageWidth - 40, responseBoxHeight, 3, 3, 'F');
            
            doc.setFontSize(10);
            doc.setFont(undefined, 'bold');
            doc.setTextColor(100, 116, 139);
            doc.text('Response:', 25, y + 10);
            doc.setFont(undefined, 'normal');
            doc.setTextColor(60, 60, 70);
            doc.text(responseLines, 25, y + 18);
            y += responseBoxHeight + 10;
          }
          
          // Security Control Requirements section - full-width boxes (formerly "MUST Statements")
          if (control.mustStatements?.length > 0) {
            doc.setFont(undefined, 'bold');
            doc.setTextColor(139, 92, 246); // Purple instead of red
            doc.setFontSize(13);
            doc.text('Security Control Requirements:', 20, y);
            y += 10;
            
            // Check if all MUST statements are marked as DOES (fully compliant)
            const allDoes = control.mustStatements.every(must => 
              typeof must === 'object' && must.status === 'DOES'
            );
            
            control.mustStatements.forEach((must, idx) => {
              // Check if we need to continue on a new page
              if (y > pageHeight - 50) {
                doc.addPage();
                addHeader(doc, pageWidth, category.name + ' (continued)');
                y = 55;
                doc.setFontSize(9);
                doc.setTextColor(148, 163, 184);
                doc.text(`Question ${controlIndex + 1} of ${category.controls.length} (continued)`, pageWidth - 65, 38);
              }
              
              // Handle both string format (static data) and object format (user data)
              let statementText = typeof must === 'string' ? must : must.statement;
              const status = typeof must === 'object' ? must.status : 'MUST';
              const isDoes = status === 'DOES';
              
              // Remove "MUST" prefix from statement text if present (redundant since color box shows status)
              statementText = statementText.replace(/^MUST\s+/i, '');
              
              // Statement row with status indicator
              const mustLines = doc.splitTextToSize(statementText, pageWidth - 80);
              const mustBoxHeight = Math.max(16, mustLines.length * 6 + 8);
              
              // Status badge - green for DOES, red for MUST
              if (isDoes) {
                doc.setFillColor(16, 185, 129); // Emerald/green
                doc.roundedRect(25, y - 2, 30, 12, 2, 2, 'F');
                doc.setTextColor(255, 255, 255);
                doc.setFontSize(9);
                doc.setFont(undefined, 'bold');
                doc.text('DOES', 30, y + 6);
              } else {
                doc.setFillColor(220, 38, 38); // Red
                doc.roundedRect(25, y - 2, 30, 12, 2, 2, 'F');
                doc.setTextColor(255, 255, 255);
                doc.setFontSize(9);
                doc.setFont(undefined, 'bold');
                doc.text('MUST', 30, y + 6);
              }
              
              // Statement text - larger font, color based on status
              doc.setFont(undefined, 'normal');
              doc.setTextColor(isDoes ? 16 : 50, isDoes ? 100 : 50, isDoes ? 80 : 60);
              doc.setFontSize(11);
              doc.text(mustLines, 62, y + 5);
              y += mustBoxHeight + 5;
            });
            
            // "This security control is fully compliant" line - based on actual status
            y += 5;
            if (allDoes && control.response?.answer === 'Y') {
              doc.setFillColor(16, 185, 129); // Green
              doc.roundedRect(25, y - 2, 30, 12, 2, 2, 'F');
              doc.setTextColor(255, 255, 255);
              doc.setFontSize(9);
              doc.setFont(undefined, 'bold');
              doc.text('YES', 32, y + 6);
              doc.setFont(undefined, 'bold');
              doc.setTextColor(16, 185, 129);
              doc.setFontSize(11);
              doc.text('This security control is fully compliant', 62, y + 5);
            } else {
              doc.setFillColor(220, 38, 38); // Red
              doc.roundedRect(25, y - 2, 30, 12, 2, 2, 'F');
              doc.setTextColor(255, 255, 255);
              doc.setFontSize(9);
              doc.setFont(undefined, 'bold');
              doc.text('NO', 34, y + 6);
              doc.setFont(undefined, 'bold');
              doc.setTextColor(220, 38, 38);
              doc.setFontSize(11);
              doc.text('This security control is fully compliant', 62, y + 5);
            }
            y += 18;
          }
          
          // Evidence Required section
          if (control.evidenceRequired?.length > 0) {
            // Check if we need to continue on a new page
            if (y > pageHeight - 60) {
              doc.addPage();
              addHeader(doc, pageWidth, category.name + ' (continued)');
              y = 55;
            }
            
            doc.setFont(undefined, 'bold');
            doc.setTextColor(59, 130, 246);
            doc.setFontSize(13);
            doc.text('Evidence Required:', 20, y);
            y += 10;
            
            doc.setFont(undefined, 'normal');
            doc.setTextColor(50, 50, 60);
            doc.setFontSize(11);
            
            control.evidenceRequired.forEach((evidence, idx) => {
              if (y > pageHeight - 25) {
                doc.addPage();
                addHeader(doc, pageWidth, category.name + ' (continued)');
                y = 55;
              }
              // Checkbox style for evidence items
              doc.setDrawColor(100, 116, 139);
              doc.rect(25, y - 4, 5, 5);
              doc.text(evidence, 35, y);
              y += 8;
            });
          }
        });
      });
      
      // NOTE: NIST SP 800-66 Controls section removed (pages 310-365 legacy information)
      // NOTE: Evidence Checklist section removed (legacy information)
      
      // ==================== PRIORITIZATION TABLE ====================
      if (prioritizationData?.data) {
        doc.addPage();
        addHeader(doc, pageWidth, 'Security Control Prioritization');
        
        y = 55;
        
        // Summary stats
        doc.setFillColor(30, 41, 59);
        doc.roundedRect(20, y - 5, pageWidth - 40, 25, 3, 3, 'F');
        
        doc.setFontSize(11);
        doc.setTextColor(255, 255, 255);
        doc.text(`Total Controls: ${prioritizationData.summary?.total || 0}`, 30, y + 5);
        doc.setTextColor(16, 185, 129);
        doc.text(`Compliant: ${prioritizationData.summary?.yesCount || 0}`, 90, y + 5);
        doc.setTextColor(239, 68, 68);
        doc.text(`Non-Compliant: ${prioritizationData.summary?.noCount || 0}`, 140, y + 5);
        doc.setTextColor(139, 92, 246);
        doc.text(`Compliance Rate: ${prioritizationData.summary?.complianceRate || 0}%`, 30, y + 15);
        
        y += 35;
        
        // Table header
        const priorityTableData = prioritizationData.data.map((item, idx) => [
          String(idx + 1),
          item.secCntrl,
          item.definition.length > 80 ? item.definition.substring(0, 77) + '...' : item.definition,
          item.yesNo,
          String(item.priority),
          String(item.sort)
        ]);
        
        autoTable(doc, {
          startY: y,
          head: [['#', 'Control', 'Definition', 'Status', 'Priority', 'Order']],
          body: priorityTableData,
          theme: 'striped',
          headStyles: { fillColor: [139, 92, 246], textColor: [255, 255, 255], fontSize: 7, fontStyle: 'bold' },
          bodyStyles: { textColor: [30, 41, 59], fontSize: 6 },
          columnStyles: {
            0: { cellWidth: 8, halign: 'center' },
            1: { cellWidth: 35 },
            2: { cellWidth: 90 },
            3: { cellWidth: 15, halign: 'center' },
            4: { cellWidth: 15, halign: 'center' },
            5: { cellWidth: 12, halign: 'center' }
          },
          margin: { left: 15, right: 15 },
          didParseCell: function(data) {
            if (data.column.index === 3 && data.section === 'body') {
              const status = data.cell.text[0];
              if (status === 'Yes') {
                data.cell.styles.textColor = [16, 185, 129]; // Green
                data.cell.styles.fontStyle = 'bold';
              } else {
                data.cell.styles.textColor = [239, 68, 68]; // Red
                data.cell.styles.fontStyle = 'bold';
              }
            }
          }
        });
      }
      
      // ==================== ADD FOOTERS ====================
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 2; i <= pageCount; i++) {
        doc.setPage(i);
        
        doc.setFillColor(248, 250, 252);
        doc.rect(0, pageHeight - 18, pageWidth, 18, 'F');
        doc.setFillColor(139, 92, 246);
        doc.rect(0, pageHeight - 18, pageWidth, 1, 'F');
        
        doc.setFontSize(8);
        doc.setTextColor(100, 116, 139);
        doc.text('Colt Cybersecurity Consultants | Aspiritech', 20, pageHeight - 8);
        doc.text(`Keystone Alliance SSP - Full Report v${sspData?.organization?.version || '1.0'}`, pageWidth / 2, pageHeight - 8, { align: 'center' });
        doc.text(`Page ${i} of ${pageCount}`, pageWidth - 25, pageHeight - 8);
      }
      
      // Save
      doc.save('Keystone_Alliance_SSP_Full_Report.pdf');
      showToast('Full Report PDF downloaded successfully!', 'success');
      
    } catch (error) {
      console.error('Full PDF generation failed:', error);
      showToast(`Full PDF generation failed: ${error.message || 'Unknown error'}. Please try again.`, 'error');
    } finally {
      setGeneratingFull(false);
    }
  };
  
  // Helper function for PDF headers
  const addHeader = (doc, pageWidth, title) => {
    doc.setFillColor(255, 255, 255);
    doc.rect(0, 0, pageWidth, 42, 'F');
    doc.setFillColor(139, 92, 246);
    doc.rect(0, 42, pageWidth, 2, 'F');
    
    // Logos text
    doc.setTextColor(80, 80, 100);
    doc.setFontSize(9);
    doc.setFont(undefined, 'bold');
    doc.text('Colt Cybersecurity Consultants', 15, 15);
    
    doc.setTextColor(80, 80, 100);
    doc.setFontSize(9);
    doc.text('Aspiritech', pageWidth - 35, 15);
    
    // Title
    doc.setTextColor(80, 80, 100);
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text(title, 15, 32);
  };
  
  // Helper function for drawing pie chart slices
  const drawPieSlice = (doc, cx, cy, radius, startAngle, endAngle, color) => {
    doc.setFillColor(...color);
    
    // Convert angles to radians
    const startRad = (startAngle * Math.PI) / 180;
    const endRad = (endAngle * Math.PI) / 180;
    
    // Create path for pie slice
    const steps = 30;
    const angleStep = (endRad - startRad) / steps;
    
    // Start at center
    let path = `${cx} ${cy} m `;
    
    // Line to start of arc
    const startX = cx + radius * Math.cos(startRad);
    const startY = cy + radius * Math.sin(startRad);
    path += `${startX - cx} ${startY - cy} l `;
    
    // Draw arc
    for (let i = 1; i <= steps; i++) {
      const angle = startRad + i * angleStep;
      const x = cx + radius * Math.cos(angle);
      const y = cy + radius * Math.sin(angle);
      if (i === 1) {
        path += `${x - startX} ${y - startY} l `;
      }
    }
    
    // For simpler approach, draw filled sectors using triangles
    doc.setFillColor(...color);
    for (let i = 0; i < steps; i++) {
      const angle1 = startRad + i * angleStep;
      const angle2 = startRad + (i + 1) * angleStep;
      
      const x1 = cx + radius * Math.cos(angle1);
      const y1 = cy + radius * Math.sin(angle1);
      const x2 = cx + radius * Math.cos(angle2);
      const y2 = cy + radius * Math.sin(angle2);
      
      // Draw triangle from center to arc segment
      doc.triangle(cx, cy, x1, y1, x2, y2, 'F');
    }
  };

  // Generate Word Document for Full Report
  const generateWordDoc = async () => {
    // Validate required data before generating
    if (!summary || !complianceScore || !detailedControls || !nistControls) {
      showToast('Unable to generate Word document: Report data not fully loaded. Please refresh the page and try again.', 'error');
      return;
    }
    
    setGeneratingWord(true);
    try {
      const children = [];
      
      // Title Page
      children.push(
        new Paragraph({
          children: [
            new TextRun({ text: "System Security Plan", bold: true, size: 56, color: "4F46E5" }),
          ],
          alignment: AlignmentType.CENTER,
          spacing: { after: 200 },
        }),
        new Paragraph({
          children: [
            new TextRun({ text: "Full Detailed Report", bold: true, size: 36, color: "6B7280" }),
          ],
          alignment: AlignmentType.CENTER,
          spacing: { after: 400 },
        }),
        new Paragraph({
          children: [
            new TextRun({ text: sspData?.organization?.name || "Keystone Alliance", bold: true, size: 32, color: "7C3AED" }),
          ],
          alignment: AlignmentType.CENTER,
          spacing: { after: 200 },
        }),
        new Paragraph({
          children: [
            new TextRun({ text: sspData?.organization?.system_name || "KA-IS", size: 24, color: "6B7280" }),
          ],
          alignment: AlignmentType.CENTER,
          spacing: { after: 600 },
        }),
        new Paragraph({
          children: [
            new TextRun({ text: "Prepared by Colt Cybersecurity Consultants", size: 20, color: "374151" }),
          ],
          alignment: AlignmentType.CENTER,
        }),
        new Paragraph({
          children: [
            new TextRun({ text: "in partnership with Aspiritech", size: 20, color: "374151" }),
          ],
          alignment: AlignmentType.CENTER,
          spacing: { after: 200 },
        }),
        new Paragraph({
          children: [
            new TextRun({ text: `Version ${sspData?.organization?.version || "1.0"} - ${sspData?.organization?.date || "February 2026"}`, size: 20, color: "6B7280" }),
          ],
          alignment: AlignmentType.CENTER,
          spacing: { after: 800 },
        }),
        new Paragraph({
          children: [
            new TextRun({ text: "This report includes:", bold: true, size: 22 }),
          ],
          spacing: { after: 100 },
        }),
        new Paragraph({
          children: [new TextRun({ text: `• ${detailedControls?.totalQuestions || 0} HIPAA Assessment Questions with Responses`, size: 20 })],
          indent: { left: 400 },
        }),
        new Paragraph({
          children: [new TextRun({ text: "• MUST Statements for each control", size: 20 })],
          indent: { left: 400 },
        }),
        new Paragraph({
          children: [new TextRun({ text: "• Evidence Requirements checklist", size: 20 })],
          indent: { left: 400 },
        }),
        new Paragraph({
          children: [new TextRun({ text: `• ${nistControls?.totalControls || 0} NIST SP 800-66 Controls`, size: 20 })],
          indent: { left: 400 },
        }),
        new Paragraph({
          children: [new TextRun({ text: "• HIPAA to NIST Control Mapping", size: 20 })],
          indent: { left: 400 },
          spacing: { after: 400 },
        }),
        new Paragraph({ children: [], pageBreakBefore: true })
      );

      // ==================== COMPLIANCE SCORE DASHBOARD ====================
      if (complianceScore) {
        children.push(
          new Paragraph({
            text: "1. HIPAA Compliance Score Dashboard",
            heading: HeadingLevel.HEADING_1,
            spacing: { after: 300 },
          })
        );

        // Overall Score
        const scoreColor = complianceScore.overallScore.percentage >= 70 ? "10B981" :
                          complianceScore.overallScore.percentage >= 40 ? "F59E0B" : "EF4444";
        
        children.push(
          new Paragraph({
            children: [
              new TextRun({ text: "Overall Compliance Score: ", bold: true, size: 28 }),
              new TextRun({ text: `${complianceScore.overallScore.percentage}%`, bold: true, size: 36, color: scoreColor }),
            ],
            spacing: { after: 200 },
          }),
          new Paragraph({
            children: [
              new TextRun({ text: complianceScore.overallScore.displayText, size: 24, color: "6B7280" }),
            ],
            spacing: { after: 300 },
          })
        );

        // Summary Stats Table
        children.push(
          new Table({
            rows: [
              new TableRow({
                children: [
                  new TableCell({
                    children: [new Paragraph({ children: [new TextRun({ text: "Total Controls", bold: true, size: 20, color: "FFFFFF" })], alignment: AlignmentType.CENTER })],
                    shading: { type: ShadingType.SOLID, color: "7C3AED" },
                    width: { size: 2500, type: WidthType.DXA },
                  }),
                  new TableCell({
                    children: [new Paragraph({ children: [new TextRun({ text: "Compliant", bold: true, size: 20, color: "FFFFFF" })], alignment: AlignmentType.CENTER })],
                    shading: { type: ShadingType.SOLID, color: "10B981" },
                    width: { size: 2500, type: WidthType.DXA },
                  }),
                  new TableCell({
                    children: [new Paragraph({ children: [new TextRun({ text: "Non-Compliant", bold: true, size: 20, color: "FFFFFF" })], alignment: AlignmentType.CENTER })],
                    shading: { type: ShadingType.SOLID, color: "EF4444" },
                    width: { size: 2500, type: WidthType.DXA },
                  }),
                  new TableCell({
                    children: [new Paragraph({ children: [new TextRun({ text: "Points Recoverable", bold: true, size: 20, color: "FFFFFF" })], alignment: AlignmentType.CENTER })],
                    shading: { type: ShadingType.SOLID, color: "F59E0B" },
                    width: { size: 2500, type: WidthType.DXA },
                  }),
                ],
              }),
              new TableRow({
                children: [
                  new TableCell({
                    children: [new Paragraph({ children: [new TextRun({ text: String(complianceScore.summary.totalControls), bold: true, size: 28 })], alignment: AlignmentType.CENTER })],
                    width: { size: 2500, type: WidthType.DXA },
                  }),
                  new TableCell({
                    children: [new Paragraph({ children: [new TextRun({ text: String(complianceScore.summary.compliant), bold: true, size: 28, color: "10B981" })], alignment: AlignmentType.CENTER })],
                    width: { size: 2500, type: WidthType.DXA },
                  }),
                  new TableCell({
                    children: [new Paragraph({ children: [new TextRun({ text: String(complianceScore.summary.nonCompliant), bold: true, size: 28, color: "EF4444" })], alignment: AlignmentType.CENTER })],
                    width: { size: 2500, type: WidthType.DXA },
                  }),
                  new TableCell({
                    children: [new Paragraph({ children: [new TextRun({ text: String(complianceScore.summary.pointsRecoverable), bold: true, size: 28, color: "F59E0B" })], alignment: AlignmentType.CENTER })],
                    width: { size: 2500, type: WidthType.DXA },
                  }),
                ],
              }),
            ],
            width: { size: 100, type: WidthType.PERCENTAGE },
          }),
          new Paragraph({ children: [], spacing: { after: 400 } })
        );

        // Control Family Breakdown Section
        children.push(
          new Paragraph({
            children: [new TextRun({ text: "Control Family Score Breakdown", bold: true, size: 24, color: "7C3AED" })],
            spacing: { before: 200, after: 200 },
          })
        );

        // Family Breakdown Table Header
        const familyTableRows = [
          new TableRow({
            children: [
              new TableCell({
                children: [new Paragraph({ children: [new TextRun({ text: "Control Family", bold: true, size: 18, color: "FFFFFF" })] })],
                shading: { type: ShadingType.SOLID, color: "7C3AED" },
                width: { size: 3500, type: WidthType.DXA },
              }),
              new TableCell({
                children: [new Paragraph({ children: [new TextRun({ text: "Score", bold: true, size: 18, color: "FFFFFF" })], alignment: AlignmentType.CENTER })],
                shading: { type: ShadingType.SOLID, color: "7C3AED" },
                width: { size: 1500, type: WidthType.DXA },
              }),
              new TableCell({
                children: [new Paragraph({ children: [new TextRun({ text: "Compliant", bold: true, size: 18, color: "FFFFFF" })], alignment: AlignmentType.CENTER })],
                shading: { type: ShadingType.SOLID, color: "7C3AED" },
                width: { size: 1500, type: WidthType.DXA },
              }),
              new TableCell({
                children: [new Paragraph({ children: [new TextRun({ text: "Non-Comp", bold: true, size: 18, color: "FFFFFF" })], alignment: AlignmentType.CENTER })],
                shading: { type: ShadingType.SOLID, color: "7C3AED" },
                width: { size: 1500, type: WidthType.DXA },
              }),
              new TableCell({
                children: [new Paragraph({ children: [new TextRun({ text: "% Complete", bold: true, size: 18, color: "FFFFFF" })], alignment: AlignmentType.CENTER })],
                shading: { type: ShadingType.SOLID, color: "7C3AED" },
                width: { size: 1500, type: WidthType.DXA },
              }),
            ],
          }),
        ];

        // Add rows for each family
        complianceScore.familyScores.forEach((family, idx) => {
          const pctColor = family.percentComplete >= 70 ? "10B981" :
                          family.percentComplete >= 40 ? "F59E0B" : "EF4444";
          const rowBg = idx % 2 === 0 ? "F8FAFC" : "FFFFFF";
          
          familyTableRows.push(
            new TableRow({
              children: [
                new TableCell({
                  children: [new Paragraph({ children: [new TextRun({ text: family.name, size: 18 })] })],
                  shading: { type: ShadingType.SOLID, color: rowBg },
                }),
                new TableCell({
                  children: [new Paragraph({ children: [new TextRun({ text: `${family.score}/${family.maxScore}`, size: 18, color: "7C3AED" })], alignment: AlignmentType.CENTER })],
                  shading: { type: ShadingType.SOLID, color: rowBg },
                }),
                new TableCell({
                  children: [new Paragraph({ children: [new TextRun({ text: String(family.compliant), size: 18, color: "10B981" })], alignment: AlignmentType.CENTER })],
                  shading: { type: ShadingType.SOLID, color: rowBg },
                }),
                new TableCell({
                  children: [new Paragraph({ children: [new TextRun({ text: String(family.nonCompliant), size: 18, color: "EF4444" })], alignment: AlignmentType.CENTER })],
                  shading: { type: ShadingType.SOLID, color: rowBg },
                }),
                new TableCell({
                  children: [new Paragraph({ children: [new TextRun({ text: `${family.percentComplete}%`, bold: true, size: 18, color: pctColor })], alignment: AlignmentType.CENTER })],
                  shading: { type: ShadingType.SOLID, color: rowBg },
                }),
              ],
            })
          );
        });

        // Add total row
        const totalPctColor = complianceScore.overallScore.percentage >= 70 ? "10B981" :
                             complianceScore.overallScore.percentage >= 40 ? "F59E0B" : "EF4444";
        familyTableRows.push(
          new TableRow({
            children: [
              new TableCell({
                children: [new Paragraph({ children: [new TextRun({ text: "TOTAL", bold: true, size: 18, color: "FFFFFF" })] })],
                shading: { type: ShadingType.SOLID, color: "1E293B" },
              }),
              new TableCell({
                children: [new Paragraph({ children: [new TextRun({ text: `${complianceScore.overallScore.score}/${complianceScore.overallScore.maxScore}`, bold: true, size: 18, color: "FFFFFF" })], alignment: AlignmentType.CENTER })],
                shading: { type: ShadingType.SOLID, color: "1E293B" },
              }),
              new TableCell({
                children: [new Paragraph({ children: [new TextRun({ text: String(complianceScore.summary.compliant), bold: true, size: 18, color: "10B981" })], alignment: AlignmentType.CENTER })],
                shading: { type: ShadingType.SOLID, color: "1E293B" },
              }),
              new TableCell({
                children: [new Paragraph({ children: [new TextRun({ text: String(complianceScore.summary.nonCompliant), bold: true, size: 18, color: "EF4444" })], alignment: AlignmentType.CENTER })],
                shading: { type: ShadingType.SOLID, color: "1E293B" },
              }),
              new TableCell({
                children: [new Paragraph({ children: [new TextRun({ text: `${complianceScore.overallScore.percentage}%`, bold: true, size: 18, color: totalPctColor })], alignment: AlignmentType.CENTER })],
                shading: { type: ShadingType.SOLID, color: "1E293B" },
              }),
            ],
          })
        );

        children.push(
          new Table({
            rows: familyTableRows,
            width: { size: 100, type: WidthType.PERCENTAGE },
          }),
          new Paragraph({ children: [], spacing: { after: 300 } })
        );

        // Scoring methodology note
        children.push(
          new Paragraph({
            children: [
              new TextRun({ text: "Scoring Methodology: ", bold: true, size: 18, color: "92400E" }),
              new TextRun({ text: "Each control = 1 point. Yes = 1pt, Partial = 0pt, No = 0pt, N/A = Excluded from calculation", size: 18, color: "78350F" }),
            ],
            shading: { type: ShadingType.SOLID, color: "FEF3C7" },
            spacing: { after: 200 },
          }),
          new Paragraph({ children: [], pageBreakBefore: true })
        );
      }

      // Executive Summary Section
      children.push(
        new Paragraph({
          text: "2. Executive Summary",
          heading: HeadingLevel.HEADING_1,
          spacing: { after: 200 },
        }),
        new Paragraph({
          children: [new TextRun({ text: sspData?.executive_summary?.overview || "", size: 22 })],
          spacing: { after: 200 },
        }),
        new Paragraph({
          children: [
            new TextRun({ text: "Compliance Framework: ", bold: true, size: 22 }),
            new TextRun({ text: sspData?.executive_summary?.compliance_framework || "NIST SP 800-66 / HIPAA", size: 22, color: "4F46E5" }),
          ],
          spacing: { after: 400 },
        })
      );

      // Assessment Summary Stats
      const stats = detailedControls?.summary || {};
      children.push(
        new Paragraph({
          text: "3. Assessment Summary",
          heading: HeadingLevel.HEADING_1,
          spacing: { after: 200 },
        }),
        new Paragraph({
          children: [
            new TextRun({ text: "Response Summary: ", bold: true, size: 22 }),
            new TextRun({ text: `Yes: ${stats.yes || 0} | `, size: 22, color: "10B981" }),
            new TextRun({ text: `Partial: ${stats.partial || 0} | `, size: 22, color: "F59E0B" }),
            new TextRun({ text: `No: ${stats.no || 0} | `, size: 22, color: "EF4444" }),
            new TextRun({ text: `N/A: ${stats.na || 0} | `, size: 22, color: "6B7280" }),
            new TextRun({ text: `No Response: ${stats.noResponse || 0}`, size: 22, color: "9CA3AF" }),
          ],
          spacing: { after: 400 },
        }),
        new Paragraph({ children: [], pageBreakBefore: true })
      );

      // HIPAA Detailed Controls
      children.push(
        new Paragraph({
          text: "4. HIPAA Assessment - Detailed Controls",
          heading: HeadingLevel.HEADING_1,
          spacing: { after: 300 },
        })
      );

      detailedControls?.categories?.forEach((category, catIdx) => {
        // Category Header
        children.push(
          new Paragraph({
            children: [
              new TextRun({ text: `3.${catIdx + 1} ${category.name}`, bold: true, size: 26, color: "7C3AED" }),
              new TextRun({ text: ` (${category.controls.length} questions)`, size: 22, color: "6B7280" }),
            ],
            spacing: { before: 300, after: 200 },
          })
        );

        category.controls.forEach((control, ctrlIdx) => {
          const answerLabels = { Y: "YES", N: "NO", P: "PARTIAL", NA: "N/A", NR: "N/R" };
          const answerColors = { Y: "10B981", N: "EF4444", P: "F59E0B", NA: "6B7280", NR: "9CA3AF" };
          const answerBgColors = { Y: "DCFCE7", N: "FEE2E2", P: "FEF3C7", NA: "F3F4F6", NR: "F3F4F6" };
          const answer = control.response?.answer || "N";  // Default to N (No) - user must demonstrate compliance

          // Question with prominent status box using a table
          children.push(
            new Table({
              rows: [
                new TableRow({
                  children: [
                    // Status Box Cell
                    new TableCell({
                      children: [
                        new Paragraph({
                          children: [
                            new TextRun({ 
                              text: answerLabels[answer], 
                              bold: true, 
                              size: 20, 
                              color: "FFFFFF"
                            }),
                          ],
                          alignment: AlignmentType.CENTER,
                        }),
                      ],
                      width: { size: 1000, type: WidthType.DXA },
                      shading: { type: ShadingType.SOLID, color: answerColors[answer] },
                      verticalAlign: "center",
                    }),
                    // Question Cell
                    new TableCell({
                      children: [
                        new Paragraph({
                          children: [
                            new TextRun({ text: `${control.id}`, bold: true, size: 18, color: "7C3AED" }),
                            new TextRun({ text: " - ", size: 18 }),
                            new TextRun({ text: control.text, size: 18 }),
                          ],
                        }),
                      ],
                      width: { size: 8500, type: WidthType.DXA },
                      shading: { type: ShadingType.SOLID, color: answerBgColors[answer] },
                    }),
                  ],
                }),
              ],
              width: { size: 100, type: WidthType.PERCENTAGE },
              borders: {
                top: { style: BorderStyle.SINGLE, size: 1, color: "D1D5DB" },
                bottom: { style: BorderStyle.SINGLE, size: 1, color: "D1D5DB" },
                left: { style: BorderStyle.SINGLE, size: 1, color: "D1D5DB" },
                right: { style: BorderStyle.SINGLE, size: 1, color: "D1D5DB" },
              },
            }),
            new Paragraph({ children: [], spacing: { after: 50 } })
          );

          // Response Text
          if (control.response?.responseText) {
            children.push(
              new Paragraph({
                children: [
                  new TextRun({ text: "Response: ", bold: true, size: 18, color: "374151" }),
                  new TextRun({ text: control.response.responseText, size: 18, italics: true, color: "374151" })
                ],
                indent: { left: 400 },
                spacing: { after: 100 },
              })
            );
          }

          // Comment
          if (control.response?.comment) {
            children.push(
              new Paragraph({
                children: [
                  new TextRun({ text: "Note: ", bold: true, size: 16, color: "6B7280" }),
                  new TextRun({ text: control.response.comment, size: 16, color: "6B7280" }),
                ],
                indent: { left: 400 },
                spacing: { after: 100 },
              })
            );
          }

          // MUST Statements with YES/NO status based on actual user data
          if (control.mustStatements?.length > 0) {
            children.push(
              new Paragraph({
                children: [new TextRun({ text: "Security Control Requirements:", bold: true, size: 20, color: "7C3AED" })],
                indent: { left: 400 },
                spacing: { before: 100, after: 50 },
              })
            );
            
            // Check if all MUST statements are marked as DOES (fully compliant)
            const allDoes = control.mustStatements.every(must => 
              typeof must === 'object' && must.status === 'DOES'
            );
            
            // Render MUST statements with their actual status
            control.mustStatements.forEach((must, idx) => {
              // Handle both string format (static data) and object format (user data)
              let statementText = typeof must === 'string' ? must : must.statement;
              const status = typeof must === 'object' ? must.status : 'MUST';
              const isDoes = status === 'DOES';
              
              // Remove "MUST" prefix from statement text if present (redundant since color box shows status)
              statementText = statementText.replace(/^MUST\s+/i, '');
              
              children.push(
                new Table({
                  rows: [
                    new TableRow({
                      children: [
                        new TableCell({
                          children: [
                            new Paragraph({
                              children: [new TextRun({ text: isDoes ? "DOES" : "MUST", bold: true, size: 18, color: "FFFFFF" })],
                              alignment: AlignmentType.CENTER,
                            }),
                          ],
                          width: { size: 700, type: WidthType.DXA },
                          shading: { type: ShadingType.SOLID, color: isDoes ? "10B981" : "DC2626" }, // Green or Red
                          verticalAlign: "center",
                        }),
                        new TableCell({
                          children: [
                            new Paragraph({
                              children: [new TextRun({ text: statementText, size: 18, color: isDoes ? "065F46" : "374151" })],
                            }),
                          ],
                          width: { size: 8300, type: WidthType.DXA },
                          shading: { type: ShadingType.SOLID, color: isDoes ? "D1FAE5" : "FEF2F2" }, // Light green or light red
                        }),
                      ],
                    }),
                  ],
                  width: { size: 100, type: WidthType.PERCENTAGE },
                  borders: {
                    top: { style: BorderStyle.SINGLE, size: 1, color: isDoes ? "86EFAC" : "FECACA" },
                    bottom: { style: BorderStyle.SINGLE, size: 1, color: isDoes ? "86EFAC" : "FECACA" },
                    left: { style: BorderStyle.SINGLE, size: 1, color: isDoes ? "86EFAC" : "FECACA" },
                    right: { style: BorderStyle.SINGLE, size: 1, color: isDoes ? "86EFAC" : "FECACA" },
                  },
                }),
                new Paragraph({ children: [], spacing: { after: 30 } })
              );
            });
            
            // Add "This security control is fully compliant" line - based on actual status
            const isFullyCompliant = allDoes && control.response?.answer === 'Y';
            children.push(
              new Paragraph({ children: [], spacing: { after: 50 } }),
              new Table({
                rows: [
                  new TableRow({
                    children: [
                      new TableCell({
                        children: [
                          new Paragraph({
                            children: [new TextRun({ text: isFullyCompliant ? "YES" : "NO", bold: true, size: 18, color: "FFFFFF" })],
                            alignment: AlignmentType.CENTER,
                          }),
                        ],
                        width: { size: 700, type: WidthType.DXA },
                        shading: { type: ShadingType.SOLID, color: isFullyCompliant ? "10B981" : "DC2626" },
                        verticalAlign: "center",
                      }),
                      new TableCell({
                        children: [
                          new Paragraph({
                            children: [new TextRun({ text: "This security control is fully compliant", bold: true, size: 20, color: isFullyCompliant ? "065F46" : "DC2626" })],
                          }),
                        ],
                        width: { size: 8300, type: WidthType.DXA },
                        shading: { type: ShadingType.SOLID, color: isFullyCompliant ? "D1FAE5" : "FEE2E2" },
                      }),
                    ],
                  }),
                ],
                width: { size: 100, type: WidthType.PERCENTAGE },
                borders: {
                  top: { style: BorderStyle.SINGLE, size: 2, color: isFullyCompliant ? "10B981" : "DC2626" },
                  bottom: { style: BorderStyle.SINGLE, size: 2, color: isFullyCompliant ? "10B981" : "DC2626" },
                  left: { style: BorderStyle.SINGLE, size: 2, color: isFullyCompliant ? "10B981" : "DC2626" },
                  right: { style: BorderStyle.SINGLE, size: 2, color: isFullyCompliant ? "10B981" : "DC2626" },
                },
              }),
              new Paragraph({ children: [], spacing: { after: 100 } })
            );
          }

          // Evidence Required
          if (control.evidenceRequired?.length > 0) {
            children.push(
              new Paragraph({
                children: [new TextRun({ text: "Evidence Required:", bold: true, size: 20, color: "2563EB" })],
                indent: { left: 400 },
                spacing: { before: 100, after: 50 },
              })
            );
            control.evidenceRequired.forEach(evidence => {
              children.push(
                new Paragraph({
                  children: [new TextRun({ text: `☐ ${evidence}`, size: 18 })],
                  indent: { left: 600 },
                  spacing: { after: 50 },
                })
              );
            });
          }

          // Add page break after each control question
          children.push(new Paragraph({ children: [], pageBreakBefore: true }));
        });

        // Category header for next section (no additional page break needed since each control has one)
      });

      // NOTE: NIST SP 800-66 Controls section removed (legacy information)
      // NOTE: Evidence Checklist section removed (legacy information)

      // ==================== PRIORITIZATION TABLE ====================
      if (prioritizationData?.data) {
        children.push(
          new Paragraph({
            children: [new TextRun({ text: "Security Control Prioritization", bold: true, size: 32, color: "7C3AED" })],
            heading: HeadingLevel.HEADING_1,
            spacing: { before: 400, after: 200 },
            pageBreakBefore: true,
          }),
          new Paragraph({
            children: [
              new TextRun({ text: `Total Controls: ${prioritizationData.summary?.total || 0}  |  `, size: 20 }),
              new TextRun({ text: `Compliant: ${prioritizationData.summary?.yesCount || 0}`, size: 20, color: "10B981", bold: true }),
              new TextRun({ text: `  |  `, size: 20 }),
              new TextRun({ text: `Non-Compliant: ${prioritizationData.summary?.noCount || 0}`, size: 20, color: "EF4444", bold: true }),
              new TextRun({ text: `  |  Compliance Rate: ${prioritizationData.summary?.complianceRate || 0}%`, size: 20, color: "7C3AED", bold: true }),
            ],
            spacing: { after: 200 },
          })
        );

        // Create prioritization table
        const priorityTableRows = [
          new TableRow({
            tableHeader: true,
            children: [
              new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: "#", bold: true, color: "FFFFFF", size: 18 })] })], shading: { fill: "7C3AED" }, width: { size: 5, type: WidthType.PERCENTAGE } }),
              new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: "Control", bold: true, color: "FFFFFF", size: 18 })] })], shading: { fill: "7C3AED" }, width: { size: 20, type: WidthType.PERCENTAGE } }),
              new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: "Definition", bold: true, color: "FFFFFF", size: 18 })] })], shading: { fill: "7C3AED" }, width: { size: 55, type: WidthType.PERCENTAGE } }),
              new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: "Status", bold: true, color: "FFFFFF", size: 18 })] })], shading: { fill: "7C3AED" }, width: { size: 8, type: WidthType.PERCENTAGE } }),
              new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: "Priority", bold: true, color: "FFFFFF", size: 18 })] })], shading: { fill: "7C3AED" }, width: { size: 6, type: WidthType.PERCENTAGE } }),
              new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: "Order", bold: true, color: "FFFFFF", size: 18 })] })], shading: { fill: "7C3AED" }, width: { size: 6, type: WidthType.PERCENTAGE } }),
            ],
          }),
          ...prioritizationData.data.map((item, idx) => 
            new TableRow({
              children: [
                new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: String(idx + 1), size: 16 })] })] }),
                new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: item.secCntrl, size: 16 })] })] }),
                new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: item.definition.length > 100 ? item.definition.substring(0, 97) + '...' : item.definition, size: 16 })] })] }),
                new TableCell({ 
                  children: [new Paragraph({ children: [new TextRun({ text: item.yesNo, size: 16, bold: true, color: item.yesNo === 'Yes' ? "10B981" : "EF4444" })] })],
                  shading: { fill: item.yesNo === 'Yes' ? "DCFCE7" : "FEE2E2" }
                }),
                new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: String(item.priority), size: 16 })] })] }),
                new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: String(item.sort), size: 16 })] })] }),
              ],
            })
          )
        ];

        children.push(
          new Table({
            rows: priorityTableRows,
            width: { size: 100, type: WidthType.PERCENTAGE },
          })
        );
      }

      // Footer
      children.push(
        new Paragraph({ children: [], spacing: { after: 400 } }),
        new Paragraph({
          children: [new TextRun({ text: "---", size: 20, color: "D1D5DB" })],
          alignment: AlignmentType.CENTER,
          spacing: { after: 200 },
        }),
        new Paragraph({
          children: [new TextRun({ text: "Colt Cybersecurity Consultants | Aspiritech", size: 18, color: "6B7280" })],
          alignment: AlignmentType.CENTER,
        }),
        new Paragraph({
          children: [new TextRun({ text: `Keystone Alliance SSP - Full Report v${sspData?.organization?.version || "1.0"}`, size: 18, color: "6B7280" })],
          alignment: AlignmentType.CENTER,
        })
      );

      // Create Document
      const doc = new Document({
        sections: [{
          properties: {},
          children: children,
        }],
      });

      // Generate and save
      const blob = await Packer.toBlob(doc);
      saveAs(blob, "Keystone_Alliance_SSP_Full_Report.docx");
      showToast('Word document downloaded successfully!', 'success');

    } catch (error) {
      console.error('Word document generation failed:', error);
      showToast(`Word document generation failed: ${error.message || 'Unknown error'}. Please try again.`, 'error');
    } finally {
      setGeneratingWord(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-violet-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 pt-16">
      {/* Toast Notification */}
      {toast && (
        <Toast 
          message={toast.message} 
          type={toast.type} 
          onClose={() => setToast(null)} 
        />
      )}
      
      {/* Data Load Error Banner */}
      {dataLoadError && (
        <div className="fixed top-16 left-0 right-0 z-40 bg-amber-600 text-white px-4 py-2 text-center text-sm">
          <AlertTriangle className="w-4 h-4 inline mr-2" />
          {dataLoadError}
          <button 
            onClick={() => { setDataLoadError(null); fetchSSPData(); }}
            className="ml-4 underline hover:no-underline"
          >
            Retry
          </button>
        </div>
      )}
      
      {/* Hero Header */}
      <header className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-violet-600/20 via-blue-600/10 to-emerald-600/20" />
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-violet-500 via-blue-500 to-emerald-500" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-start justify-between">
            {/* Back Button & Title */}
            <div className="flex items-start gap-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="mt-1 p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
                data-testid="back-button"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                {/* Co-branding */}
                <div className="flex items-center gap-4 mb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-bold text-white">Colt Cybersecurity Consultants</span>
                  </div>
                  <div className="w-px h-6 bg-slate-700" />
                  <img 
                    src={ASPIRITECH_LOGO_URL} 
                    alt="Aspiritech" 
                    className="h-8 object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'block';
                    }}
                  />
                  <span className="text-lg font-semibold text-white hidden">Aspiritech</span>
                </div>
                
                <h1 className="text-3xl font-bold text-white" data-testid="ssp-report-title">
                  System Security Plan
                </h1>
                <p className="text-lg text-violet-300 mt-1">
                  {sspData?.organization?.name || 'Keystone Alliance'}
                </p>
                <p className="text-sm text-slate-400 mt-1">
                  {sspData?.organization?.system_name} • Version {sspData?.organization?.version} • {sspData?.organization?.date}
                </p>
              </div>
            </div>
            
            {/* Download Buttons */}
            <div className="flex items-center gap-3">
              <button
                onClick={generatePDF}
                disabled={generating}
                className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-medium transition-all disabled:opacity-50"
                data-testid="download-summary-pdf"
              >
                {generating ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    Executive Summary
                  </>
                )}
              </button>
              <button
                onClick={generateFullPDF}
                disabled={generatingFull}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-violet-600 to-blue-600 hover:from-violet-500 hover:to-blue-500 text-white rounded-lg font-medium transition-all shadow-lg shadow-violet-500/25 disabled:opacity-50"
                data-testid="download-full-pdf"
              >
                {generatingFull ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <FileText className="w-4 h-4" />
                    Full Report PDF
                  </>
                )}
              </button>
              <button
                onClick={generateWordDoc}
                disabled={generatingWord}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg font-medium transition-all disabled:opacity-50"
                data-testid="download-word-doc"
              >
                {generatingWord ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <FileDown className="w-4 h-4" />
                    Word Document
                  </>
                )}
              </button>
              <button
                onClick={() => navigate(`/ssp-full-report${assessmentId ? `?assessmentId=${assessmentId}` : ''}`)}
                className="flex items-center gap-2 px-4 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded-lg font-medium transition-all"
                data-testid="view-full-report"
              >
                <Globe className="w-4 h-4" />
                View Web Report
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-xl p-5" data-testid="stat-implementation">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-violet-500/20 rounded-lg flex items-center justify-center">
                <Activity className="w-5 h-5 text-violet-400" />
              </div>
              <span className="text-slate-400 text-sm">Implementation</span>
            </div>
            <div className="text-3xl font-bold text-white">{summary?.implementation_rate || 0}%</div>
          </div>
          
          <div className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-xl p-5" data-testid="stat-controls">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-blue-400" />
              </div>
              <span className="text-slate-400 text-sm">Total Controls</span>
            </div>
            <div className="text-3xl font-bold text-white">{summary?.controls?.total || 0}</div>
          </div>
          
          <div className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-xl p-5" data-testid="stat-systems">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                <Server className="w-5 h-5 text-emerald-400" />
              </div>
              <span className="text-slate-400 text-sm">Core Systems</span>
            </div>
            <div className="text-3xl font-bold text-white">{summary?.core_systems_count || 0}</div>
          </div>
          
          <div className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-xl p-5" data-testid="stat-providers">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-amber-400" />
              </div>
              <span className="text-slate-400 text-sm">Service Providers</span>
            </div>
            <div className="text-3xl font-bold text-white">{summary?.service_providers_count || 0}</div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {[
            { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
            { id: 'score', label: 'Compliance Score', icon: Activity },
            { id: 'overview', label: 'Overview', icon: FileText },
            { id: 'systems', label: 'Systems', icon: Server },
            { id: 'controls', label: 'Security Controls', icon: Lock },
            { id: 'detailed', label: 'Detailed Controls', icon: ClipboardList },
            { id: 'dataflow', label: 'Data Flow', icon: Network },
            { id: 'prioritization', label: 'Prioritization', icon: ListOrdered },
            { id: 'priorities', label: 'Priorities', icon: AlertTriangle }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-violet-600 text-white shadow-lg shadow-violet-500/25'
                  : 'bg-slate-800/50 text-slate-400 hover:text-white hover:bg-slate-700/50'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'dashboard' && (
          <SspDashboard 
            complianceScore={complianceScore}
            detailedControls={detailedControls}
            evidenceChecklist={evidenceChecklist}
            summary={summary}
            assessmentId={assessmentId}
          />
        )}

        {activeTab === 'score' && complianceScore && (
          <div className="space-y-6" data-testid="compliance-score-tab">
            {/* Score Header Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Main Score Card */}
              <div className="lg:col-span-2 bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700/50 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                    <Activity className="w-5 h-5 text-violet-400" />
                    HIPAA Compliance Score
                  </h2>
                </div>
                <div className="flex items-end gap-4">
                  <div className="flex-1">
                    <div className={`text-5xl font-bold mb-2 ${
                      complianceScore.overallScore.percentage >= 70 ? 'text-emerald-400' :
                      complianceScore.overallScore.percentage >= 40 ? 'text-amber-400' : 'text-red-400'
                    }`}>
                      {complianceScore.overallScore.percentage}%
                    </div>
                    <div className="text-slate-400">
                      {complianceScore.overallScore.displayText}
                    </div>
                  </div>
                  {/* Score Gauge */}
                  <div className="w-32 h-32">
                    <ResponsiveContainer width="100%" height="100%">
                      <RadialBarChart 
                        cx="50%" 
                        cy="50%" 
                        innerRadius="60%" 
                        outerRadius="100%" 
                        barSize={12} 
                        data={[{ value: complianceScore.overallScore.percentage, fill: complianceScore.overallScore.percentage >= 70 ? '#10B981' : complianceScore.overallScore.percentage >= 40 ? '#F59E0B' : '#EF4444' }]}
                        startAngle={180}
                        endAngle={0}
                      >
                        <RadialBar background={{ fill: '#1E293B' }} dataKey="value" cornerRadius={10} />
                      </RadialBarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-slate-700/50">
                  <div className="text-sm text-slate-500">
                    Scoring: 1 point per control (Yes = 1pt, Partial/No = 0pt)
                  </div>
                </div>
              </div>

              {/* Summary Stats */}
              <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
                <h3 className="text-sm font-medium text-slate-400 mb-4">Control Status</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-emerald-400 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" /> Compliant
                    </span>
                    <span className="text-white font-bold">{complianceScore.summary.compliant}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-red-400 flex items-center gap-2">
                      <XCircle className="w-4 h-4" /> Non-Compliant
                    </span>
                    <span className="text-white font-bold">{complianceScore.summary.nonCompliant}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400 flex items-center gap-2">
                      <MinusCircle className="w-4 h-4" /> Not Applicable
                    </span>
                    <span className="text-white font-bold">{complianceScore.summary.notApplicable}</span>
                  </div>
                </div>
              </div>

              {/* Points Recoverable */}
              <div className="bg-gradient-to-br from-amber-900/30 to-amber-800/20 border border-amber-500/30 rounded-xl p-6">
                <h3 className="text-sm font-medium text-amber-400 mb-2">Points Recoverable</h3>
                <div className="text-4xl font-bold text-white mb-2">
                  {complianceScore.summary.pointsRecoverable}
                </div>
                <div className="text-sm text-amber-300/70">
                  Address gaps to improve score
                </div>
                <div className="mt-4 h-2 bg-amber-900/50 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-amber-500 rounded-full transition-all duration-500"
                    style={{ width: `${100 - complianceScore.overallScore.percentage}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Charts Row */}
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Compliance Pie Chart */}
              <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-white">Overall Compliance Distribution</h3>
                  <button
                    onClick={() => exportChartAsJPG('pie-chart', 'compliance_distribution')}
                    className="flex items-center gap-1 px-2 py-1 text-xs bg-slate-700/50 hover:bg-slate-600/50 text-slate-300 rounded transition-colors"
                    title="Export as JPG"
                    data-testid="export-pie-chart"
                  >
                    <Image className="w-3 h-3" /> JPG
                  </button>
                </div>
                <div className="h-64" data-chart-id="pie-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={complianceScore.chartData.pieChart}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={90}
                        paddingAngle={2}
                        dataKey="value"
                        label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
                        labelLine={false}
                      >
                        {complianceScore.chartData.pieChart.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#1E293B', border: '1px solid #475569', borderRadius: '8px' }}
                        itemStyle={{ color: '#E2E8F0' }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex justify-center gap-6 mt-4">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-emerald-500" />
                    <span className="text-slate-300 text-sm">Compliant</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-500" />
                    <span className="text-slate-300 text-sm">Non-Compliant</span>
                  </div>
                </div>
              </div>

              {/* Family Bar Chart */}
              <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-white">Compliance by Control Family</h3>
                  <button
                    onClick={() => exportChartAsJPG('bar-chart', 'compliance_by_family')}
                    className="flex items-center gap-1 px-2 py-1 text-xs bg-slate-700/50 hover:bg-slate-600/50 text-slate-300 rounded transition-colors"
                    title="Export as JPG"
                    data-testid="export-bar-chart"
                  >
                    <Image className="w-3 h-3" /> JPG
                  </button>
                </div>
                <div className="h-64" data-chart-id="bar-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart 
                      data={complianceScore.chartData.barChart.slice(0, 8)} 
                      layout="vertical"
                      margin={{ left: 10, right: 30 }}
                    >
                      <XAxis type="number" stroke="#64748B" fontSize={10} />
                      <YAxis 
                        type="category" 
                        dataKey="family" 
                        stroke="#64748B" 
                        fontSize={10} 
                        width={100}
                        tickLine={false}
                      />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#1E293B', border: '1px solid #475569', borderRadius: '8px' }}
                        itemStyle={{ color: '#E2E8F0' }}
                        formatter={(value, name) => [value, name === 'compliant' ? 'Compliant' : 'Non-Compliant']}
                        labelFormatter={(label) => complianceScore.chartData.barChart.find(b => b.family === label)?.fullName || label}
                      />
                      <Bar dataKey="compliant" stackId="a" fill="#10B981" radius={[0, 0, 0, 0]} />
                      <Bar dataKey="nonCompliant" stackId="a" fill="#EF4444" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* Family Score Breakdown Table */}
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden">
              <div className="p-4 border-b border-slate-700/50">
                <h3 className="text-lg font-semibold text-white">Control Family Score Breakdown</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-900/50">
                      <th className="text-left p-4 text-slate-400 font-medium">Control Family</th>
                      <th className="text-center p-4 text-slate-400 font-medium">Score</th>
                      <th className="text-center p-4 text-emerald-400 font-medium">Compliant</th>
                      <th className="text-center p-4 text-red-400 font-medium">Non-Compliant</th>
                      <th className="text-center p-4 text-slate-400 font-medium">Total</th>
                      <th className="text-center p-4 text-slate-400 font-medium">% Complete</th>
                      <th className="p-4 text-slate-400 font-medium">Progress</th>
                    </tr>
                  </thead>
                  <tbody>
                    {complianceScore.familyScores.map((family, idx) => (
                      <tr key={idx} className="border-t border-slate-700/30 hover:bg-slate-700/20 transition-colors">
                        <td className="p-4 text-white font-medium">{family.name}</td>
                        <td className="p-4 text-center">
                          <span className="text-violet-400 font-bold">{family.score}</span>
                          <span className="text-slate-500">/{family.maxScore}</span>
                        </td>
                        <td className="p-4 text-center text-emerald-400 font-medium">{family.compliant}</td>
                        <td className="p-4 text-center text-red-400 font-medium">{family.nonCompliant}</td>
                        <td className="p-4 text-center text-slate-300">{family.applicableControls}</td>
                        <td className="p-4 text-center">
                          <span className={`font-medium ${
                            family.percentComplete >= 70 ? 'text-emerald-400' :
                            family.percentComplete >= 40 ? 'text-amber-400' : 'text-red-400'
                          }`}>
                            {family.percentComplete}%
                          </span>
                        </td>
                        <td className="p-4">
                          <div className="w-32 h-2 bg-slate-700 rounded-full overflow-hidden">
                            <div 
                              className={`h-full rounded-full transition-all duration-500 ${
                                family.percentComplete >= 70 ? 'bg-emerald-500' :
                                family.percentComplete >= 40 ? 'bg-amber-500' : 'bg-red-500'
                              }`}
                              style={{ width: `${family.percentComplete}%` }}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-slate-900/50 border-t border-slate-600">
                      <td className="p-4 text-white font-bold">TOTAL</td>
                      <td className="p-4 text-center">
                        <span className="text-violet-400 font-bold">{complianceScore.overallScore.score}</span>
                        <span className="text-slate-500">/{complianceScore.overallScore.maxScore}</span>
                      </td>
                      <td className="p-4 text-center text-emerald-400 font-bold">{complianceScore.summary.compliant}</td>
                      <td className="p-4 text-center text-red-400 font-bold">{complianceScore.summary.nonCompliant}</td>
                      <td className="p-4 text-center text-slate-300 font-bold">{complianceScore.summary.applicableControls}</td>
                      <td className="p-4 text-center">
                        <span className={`font-bold ${
                          complianceScore.overallScore.percentage >= 70 ? 'text-emerald-400' :
                          complianceScore.overallScore.percentage >= 40 ? 'text-amber-400' : 'text-red-400'
                        }`}>
                          {complianceScore.overallScore.percentage}%
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="w-32 h-2 bg-slate-700 rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full transition-all duration-500 ${
                              complianceScore.overallScore.percentage >= 70 ? 'bg-emerald-500' :
                              complianceScore.overallScore.percentage >= 40 ? 'bg-amber-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${complianceScore.overallScore.percentage}%` }}
                          />
                        </div>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'overview' && (
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Executive Summary */}
            <div className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-xl p-6" data-testid="executive-summary">
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-violet-400" />
                Executive Summary
              </h2>
              <p className="text-slate-300 leading-relaxed mb-4">
                {sspData?.executive_summary?.overview}
              </p>
              <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                <p className="text-blue-300 text-sm">
                  <strong>Compliance Framework:</strong> {sspData?.executive_summary?.compliance_framework}
                </p>
              </div>
            </div>

            {/* Controls Chart */}
            <div className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-xl p-6" data-testid="controls-chart">
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-blue-400" />
                Control Implementation Status
              </h2>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={controlsChartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      dataKey="value"
                      paddingAngle={3}
                    >
                      {controlsChartData.map((entry, index) => (
                        <Cell key={index} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#1e293b', 
                        border: '1px solid #334155',
                        borderRadius: '8px'
                      }}
                      itemStyle={{ color: '#e2e8f0' }}
                    />
                    <Legend 
                      wrapperStyle={{ paddingTop: '20px' }}
                      formatter={(value) => <span className="text-slate-300">{value}</span>}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Core Systems */}
            <div className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-xl p-6" data-testid="core-systems">
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Cloud className="w-5 h-5 text-emerald-400" />
                Core Information Systems
              </h2>
              <div className="space-y-3">
                {sspData?.executive_summary?.core_systems?.map((system, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 bg-slate-700/30 rounded-lg">
                    <Database className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                    <span className="text-slate-200 text-sm">{system}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Service Providers */}
            <div className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-xl p-6" data-testid="service-providers">
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-amber-400" />
                Key Service Providers
              </h2>
              <div className="grid gap-3">
                {sspData?.executive_summary?.service_providers?.map((provider, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg">
                    <span className="text-white font-medium">{provider.name}</span>
                    <span className="text-slate-400 text-sm">{provider.service}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'systems' && (
          <div className="space-y-6">
            {/* System Identification */}
            <div className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-xl p-6" data-testid="system-identification">
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Cpu className="w-5 h-5 text-violet-400" />
                System Identification
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-medium text-violet-400 mb-3">General Support Systems</h3>
                  <div className="space-y-2">
                    {sspData?.system_identification?.system_type?.general_support?.map((sys, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-slate-300 text-sm">
                        <div className="w-1.5 h-1.5 bg-violet-400 rounded-full" />
                        {sys}
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-blue-400 mb-3">Major Applications</h3>
                  <div className="space-y-2">
                    {sspData?.system_identification?.system_type?.major_applications?.map((app, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-slate-300 text-sm">
                        <div className="w-1.5 h-1.5 bg-blue-400 rounded-full" />
                        {app}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Operational Status */}
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-5" data-testid="operational-items">
                <h3 className="text-emerald-400 font-semibold mb-3 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  Operational ({sspData?.operational_status?.operational?.length || 0})
                </h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {sspData?.operational_status?.operational?.map((item, idx) => (
                    <div key={idx} className="text-slate-300 text-sm py-1 border-b border-slate-700/50 last:border-0">
                      {item}
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-5" data-testid="development-items">
                <h3 className="text-blue-400 font-semibold mb-3 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Under Development ({sspData?.operational_status?.under_development?.length || 0})
                </h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {sspData?.operational_status?.under_development?.map((item, idx) => (
                    <div key={idx} className="text-slate-300 text-sm py-1 border-b border-slate-700/50 last:border-0">
                      {item}
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-5" data-testid="modification-items">
                <h3 className="text-amber-400 font-semibold mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Major Modifications ({sspData?.operational_status?.major_modifications?.length || 0})
                </h3>
                <div className="space-y-2">
                  {sspData?.operational_status?.major_modifications?.map((item, idx) => (
                    <div key={idx} className="text-slate-300 text-sm py-1 border-b border-slate-700/50 last:border-0">
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Physical Locations */}
            <div className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-xl p-6" data-testid="physical-locations">
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-emerald-400" />
                Physical Locations
              </h2>
              <div className="grid md:grid-cols-3 gap-4">
                {sspData?.system_environment?.physical_locations?.map((loc, idx) => (
                  <div key={idx} className="p-4 bg-slate-700/30 rounded-lg">
                    <h4 className="text-white font-medium mb-2">{loc.name}</h4>
                    <div className="space-y-1">
                      {loc.features.map((feat, fidx) => (
                        <div key={fidx} className="text-slate-400 text-sm flex items-center gap-1">
                          <ChevronRight className="w-3 h-3" />
                          {feat}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'controls' && (
          <div className="space-y-6">
            {/* Controls Summary Bar */}
            <div className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-xl p-6" data-testid="controls-summary-bar">
              <h2 className="text-lg font-semibold text-white mb-4">Control Status Summary</h2>
              <div className="h-12 bg-slate-700/50 rounded-lg overflow-hidden flex">
                {controlsChartData.map((item, idx) => (
                  <div
                    key={idx}
                    className="h-full flex items-center justify-center text-xs font-medium text-white transition-all"
                    style={{ 
                      width: `${(item.value / (summary?.controls?.total || 1)) * 100}%`,
                      backgroundColor: item.color
                    }}
                  >
                    {item.value > 0 && item.value}
                  </div>
                ))}
              </div>
              <div className="flex justify-between mt-3">
                {controlsChartData.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                    <span className="text-slate-400 text-sm">{item.name}: {item.value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Access Controls Detail */}
            <div className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-xl p-6" data-testid="access-controls">
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Lock className="w-5 h-5 text-violet-400" />
                Access Control
                <span className={`ml-2 px-2 py-0.5 rounded text-xs ${getStatusBg(sspData?.security_controls?.access_control?.status)}`}>
                  {sspData?.security_controls?.access_control?.status}
                </span>
              </h2>
              <div className="space-y-4">
                {sspData?.security_controls?.access_control?.controls?.map((control, idx) => (
                  <div key={idx} className="border border-slate-700 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <span className="text-violet-400 font-mono text-sm">{control.id}</span>
                        <h4 className="text-white font-medium">{control.title}</h4>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs ${getStatusBg(control.status)} ${getStatusColor(control.status)}`}>
                        {control.status}
                      </span>
                    </div>
                    <div className="space-y-1">
                      {control.implementation?.map((impl, iidx) => (
                        <div key={iidx} className="text-slate-400 text-sm flex items-start gap-2">
                          <ChevronRight className="w-3 h-3 mt-1 flex-shrink-0 text-slate-500" />
                          {impl}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Other Control Categories */}
            <div className="grid md:grid-cols-2 gap-6">
              {Object.entries(sspData?.security_controls || {}).filter(([key]) => key !== 'access_control').map(([key, value]) => (
                <div key={key} className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-xl p-5" data-testid={`control-${key}`}>
                  <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                    <Shield className="w-4 h-4 text-blue-400" />
                    {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    <span className={`ml-auto px-2 py-0.5 rounded text-xs ${getStatusBg(value.status)}`}>
                      {value.status}
                    </span>
                  </h3>
                  <div className="space-y-2">
                    {value.items?.slice(0, 4).map((item, idx) => (
                      <div key={idx} className="text-slate-300 text-sm flex items-start gap-2">
                        <div className="w-1.5 h-1.5 bg-blue-400 rounded-full mt-1.5 flex-shrink-0" />
                        {item}
                      </div>
                    ))}
                    {value.items?.length > 4 && (
                      <div className="text-slate-500 text-sm">+{value.items.length - 4} more items</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'dataflow' && (
          <div className="space-y-6">
            {/* Data Flow Process */}
            <div className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-xl p-6" data-testid="dataflow-process">
              <h2 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
                <Activity className="w-5 h-5 text-violet-400" />
                Data Flow Process
              </h2>
              <div className="relative">
                {sspData?.data_flow?.steps?.map((step, idx) => (
                  <div key={idx} className="flex gap-4 mb-6 last:mb-0">
                    <div className="flex flex-col items-center">
                      <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-blue-500 rounded-full flex items-center justify-center text-white font-bold shadow-lg shadow-violet-500/30">
                        {idx + 1}
                      </div>
                      {idx < sspData?.data_flow?.steps?.length - 1 && (
                        <div className="w-0.5 h-full bg-gradient-to-b from-violet-500 to-transparent mt-2" />
                      )}
                    </div>
                    <div className="flex-1 pb-6">
                      <p className="text-slate-200">{step}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* System Interconnections */}
            <div className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-xl p-6" data-testid="interconnections">
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Globe className="w-5 h-5 text-emerald-400" />
                System Interconnections
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">System/Entity</th>
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Type</th>
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Security Mechanism</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sspData?.data_flow?.interconnections?.map((conn, idx) => (
                      <tr key={idx} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                        <td className="py-3 px-4 text-white">{conn.system}</td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded text-xs ${
                            conn.type === 'Internal' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-blue-500/20 text-blue-400'
                          }`}>
                            {conn.type}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-slate-300">{conn.security}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'detailed' && (
          <div className="space-y-6">
            {/* Header with stats and search */}
            <div className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-xl p-6" data-testid="detailed-header">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
                <div>
                  <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                    <ClipboardList className="w-6 h-6 text-violet-400" />
                    Detailed HIPAA Controls Assessment
                  </h2>
                  <p className="text-slate-400 mt-1">
                    {detailedControls?.totalQuestions || 0} questions with MUST statements and evidence requirements
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  {/* Response Stats */}
                  <div className="flex items-center gap-2 px-3 py-2 bg-emerald-500/20 rounded-lg">
                    <CheckCircle className="w-4 h-4 text-emerald-400" />
                    <span className="text-emerald-400 font-medium">{detailedControls?.summary?.yes || 0}</span>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-2 bg-amber-500/20 rounded-lg">
                    <Clock className="w-4 h-4 text-amber-400" />
                    <span className="text-amber-400 font-medium">{detailedControls?.summary?.partial || 0}</span>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-2 bg-red-500/20 rounded-lg">
                    <XCircle className="w-4 h-4 text-red-400" />
                    <span className="text-red-400 font-medium">{detailedControls?.summary?.no || 0}</span>
                  </div>
                </div>
              </div>

              {/* Search and Filter */}
              <div className="flex flex-col gap-3">
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Search questions..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-violet-500"
                      data-testid="search-questions"
                    />
                  </div>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-violet-500"
                    data-testid="filter-status"
                  >
                    <option value="all">All Responses</option>
                    <option value="Y">Yes</option>
                    <option value="P">Partial</option>
                    <option value="N">No</option>
                    <option value="NA">N/A</option>
                    <option value="NR">No Response</option>
                  </select>
                </div>
                
                {/* Effort, Priority Filters and Sort */}
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-2">
                    <span className="text-slate-400 text-sm">Effort:</span>
                    <select
                      value={filterEffort}
                      onChange={(e) => setFilterEffort(e.target.value)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium border focus:outline-none focus:ring-2 focus:ring-violet-500 ${
                        filterEffort === 'Low' ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/50' :
                        filterEffort === 'Medium' ? 'bg-amber-500/20 text-amber-400 border-amber-500/50' :
                        filterEffort === 'High' ? 'bg-red-500/20 text-red-400 border-red-500/50' :
                        'bg-slate-700/50 text-slate-300 border-slate-600'
                      }`}
                      data-testid="filter-effort"
                    >
                      <option value="all">All</option>
                      <option value="Low">Low</option>
                      <option value="Medium">Medium</option>
                      <option value="High">High</option>
                      <option value="unset">Unset</option>
                    </select>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <span className="text-slate-400 text-sm">Priority:</span>
                    <select
                      value={filterPriority}
                      onChange={(e) => setFilterPriority(e.target.value)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium border focus:outline-none focus:ring-2 focus:ring-violet-500 ${
                        filterPriority === 'Low' ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/50' :
                        filterPriority === 'Medium' ? 'bg-amber-500/20 text-amber-400 border-amber-500/50' :
                        filterPriority === 'High' ? 'bg-red-500/20 text-red-400 border-red-500/50' :
                        'bg-slate-700/50 text-slate-300 border-slate-600'
                      }`}
                      data-testid="filter-priority"
                    >
                      <option value="all">All</option>
                      <option value="Low">Low</option>
                      <option value="Medium">Medium</option>
                      <option value="High">High</option>
                      <option value="unset">Unset</option>
                    </select>
                  </div>
                  
                  <div className="flex items-center gap-2 ml-auto">
                    <span className="text-slate-400 text-sm">Sort:</span>
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="px-3 py-1.5 bg-slate-700/50 border border-slate-600 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-violet-500"
                      data-testid="sort-by"
                    >
                      <option value="default">Default Order</option>
                      <option value="effort-high">Effort: High First</option>
                      <option value="effort-low">Effort: Low First</option>
                      <option value="priority-high">Priority: High First</option>
                      <option value="priority-low">Priority: Low First</option>
                    </select>
                  </div>
                  
                  {/* Clear Filters */}
                  {(filterStatus !== 'all' || filterEffort !== 'all' || filterPriority !== 'all' || sortBy !== 'default') && (
                    <button
                      onClick={() => {
                        setFilterStatus('all');
                        setFilterEffort('all');
                        setFilterPriority('all');
                        setSortBy('default');
                      }}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                      data-testid="clear-filters-detailed"
                    >
                      <X className="w-4 h-4" /> Clear
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Questions by Category */}
            {detailedControls?.categories?.map((category, catIdx) => {
              // Filter controls
              let filteredControls = category.controls.filter(control => {
                const matchesSearch = searchQuery === '' || 
                  control.text.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  control.id.toLowerCase().includes(searchQuery.toLowerCase());
                const matchesFilter = filterStatus === 'all' || control.response.answer === filterStatus;
                const matchesEffort = filterEffort === 'all' || 
                  (filterEffort === 'unset' ? !control.levelOfEffort : control.levelOfEffort === filterEffort);
                const matchesPriority = filterPriority === 'all' || 
                  (filterPriority === 'unset' ? !control.levelOfPriority : control.levelOfPriority === filterPriority);
                return matchesSearch && matchesFilter && matchesEffort && matchesPriority;
              });
              
              // Sort controls
              if (sortBy !== 'default') {
                const levelOrder = { 'High': 3, 'Medium': 2, 'Low': 1, '': 0 };
                filteredControls = [...filteredControls].sort((a, b) => {
                  if (sortBy === 'effort-high') {
                    return (levelOrder[b.levelOfEffort] || 0) - (levelOrder[a.levelOfEffort] || 0);
                  } else if (sortBy === 'effort-low') {
                    return (levelOrder[a.levelOfEffort] || 0) - (levelOrder[b.levelOfEffort] || 0);
                  } else if (sortBy === 'priority-high') {
                    return (levelOrder[b.levelOfPriority] || 0) - (levelOrder[a.levelOfPriority] || 0);
                  } else if (sortBy === 'priority-low') {
                    return (levelOrder[a.levelOfPriority] || 0) - (levelOrder[b.levelOfPriority] || 0);
                  }
                  return 0;
                });
              }

              if (filteredControls.length === 0) return null;

              return (
                <div key={catIdx} className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-xl overflow-hidden" data-testid={`category-${catIdx}`}>
                  {/* Category Header */}
                  <button
                    onClick={() => toggleCategory(category.name)}
                    className="w-full flex items-center justify-between p-4 hover:bg-slate-700/30 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <Shield className="w-5 h-5 text-violet-400" />
                      <span className="text-white font-semibold">{category.name}</span>
                      <span className="text-slate-400 text-sm">({filteredControls.length} questions)</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-emerald-400">{category.stats.yes} Yes</span>
                        <span className="text-amber-400">{category.stats.partial} Partial</span>
                        <span className="text-red-400">{category.stats.no} No</span>
                      </div>
                      <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${expandedCategories[category.name] ? 'rotate-180' : ''}`} />
                    </div>
                  </button>

                  {/* Category Controls */}
                  {expandedCategories[category.name] && (
                    <div className="border-t border-slate-700">
                      {filteredControls.map((control, ctrlIdx) => (
                        <div key={ctrlIdx} className="border-b border-slate-700/50 last:border-0">
                          <div className="p-4">
                            {/* Question Header */}
                            <div className="flex items-start gap-4 mb-3">
                              <span className="px-2 py-1 bg-violet-500/20 text-violet-400 text-xs font-mono rounded">
                                {control.id}
                              </span>
                              <div className="flex-1">
                                <p className="text-white font-medium">{control.text}</p>
                                {control.subCategory && (
                                  <span className="text-slate-400 text-sm">{control.subCategory}</span>
                                )}
                              </div>
                              <div className={`flex items-center gap-2 px-3 py-1 rounded-lg border ${getAnswerBg(control.response.answer)}`}>
                                {getAnswerIcon(control.response.answer)}
                                <span className="text-sm font-medium">{getAnswerLabel(control.response.answer)}</span>
                              </div>
                            </div>
                            
                            {/* Level of Effort and Priority Badges */}
                            {(control.levelOfEffort || control.levelOfPriority) && (
                              <div className="flex items-center gap-3 ml-16 mb-3">
                                {control.levelOfEffort && (
                                  <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium ${
                                    control.levelOfEffort === 'Low' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' :
                                    control.levelOfEffort === 'Medium' ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' :
                                    'bg-red-500/20 text-red-400 border border-red-500/30'
                                  }`}>
                                    <span className="opacity-70">Effort:</span>
                                    <span className="font-semibold">{control.levelOfEffort}</span>
                                  </div>
                                )}
                                {control.levelOfPriority && (
                                  <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium ${
                                    control.levelOfPriority === 'Low' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' :
                                    control.levelOfPriority === 'Medium' ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' :
                                    'bg-red-500/20 text-red-400 border border-red-500/30'
                                  }`}>
                                    <span className="opacity-70">Priority:</span>
                                    <span className="font-semibold">{control.levelOfPriority}</span>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Response */}
                            {control.response.responseText && (
                              <div className="ml-16 mb-3 p-3 bg-slate-700/30 rounded-lg">
                                <p className="text-slate-300 text-sm">{control.response.responseText}</p>
                                {control.response.comment && (
                                  <p className="text-slate-400 text-xs mt-2 italic">{control.response.comment}</p>
                                )}
                              </div>
                            )}

                            {/* Security Control Requirements (formerly MUST Statements) */}
                            <div className="ml-16 mb-3">
                              <h4 className="text-violet-400 text-sm font-semibold mb-2 flex items-center gap-2">
                                <AlertTriangle className="w-4 h-4" />
                                Security Control Requirements
                              </h4>
                              <ul className="space-y-1">
                                {control.mustStatements?.map((must, mustIdx) => {
                                  // Handle both string format (static data) and object format (user data)
                                  let statementText = typeof must === 'string' ? must : must.statement;
                                  const status = typeof must === 'object' ? must.status : 'MUST';
                                  const isDoes = status === 'DOES';
                                  
                                  // Remove "MUST" prefix from statement text (redundant since badge shows status)
                                  statementText = statementText.replace(/^MUST\s+/i, '');
                                  
                                  return (
                                    <li key={mustIdx} className="text-sm flex items-start gap-2">
                                      <span className={`px-1.5 py-0.5 rounded text-xs font-semibold ${
                                        isDoes 
                                          ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' 
                                          : 'bg-red-500/20 text-red-400 border border-red-500/30'
                                      }`}>
                                        {isDoes ? 'DOES' : 'MUST'}
                                      </span>
                                      <span className={isDoes ? 'text-emerald-300' : 'text-slate-300'}>
                                        {statementText}
                                      </span>
                                    </li>
                                  );
                                })}
                              </ul>
                            </div>

                            {/* Evidence Required */}
                            <div className="ml-16">
                              <h4 className="text-blue-400 text-sm font-semibold mb-2 flex items-center gap-2">
                                <FileCheck className="w-4 h-4" />
                                Evidence Required
                              </h4>
                              <ul className="grid sm:grid-cols-2 gap-1">
                                {control.evidenceRequired?.map((evidence, evIdx) => (
                                  <li key={evIdx} className="text-slate-400 text-sm flex items-start gap-2">
                                    <span className="text-blue-400 mt-0.5">□</span>
                                    {evidence}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            {/* NIST 800-66 Controls Section */}
            <div className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-xl p-6" data-testid="nist-controls-section">
              <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                <Shield className="w-6 h-6 text-blue-400" />
                NIST SP 800-66 Controls
              </h2>
              <p className="text-slate-400 mb-6">
                {nistControls?.totalControls || 0} controls mapped to HIPAA requirements
              </p>

              <div className="space-y-4">
                {nistControls?.families?.map((family, famIdx) => (
                  <div key={famIdx} className="border border-slate-700 rounded-lg overflow-hidden">
                    <button
                      onClick={() => toggleCategory(`nist-${family.name}`)}
                      className="w-full flex items-center justify-between p-4 bg-slate-700/30 hover:bg-slate-700/50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <Lock className="w-5 h-5 text-blue-400" />
                        <span className="text-white font-medium">{family.name}</span>
                        <span className="text-slate-400 text-sm">({family.count} controls)</span>
                      </div>
                      <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${expandedCategories[`nist-${family.name}`] ? 'rotate-180' : ''}`} />
                    </button>

                    {expandedCategories[`nist-${family.name}`] && (
                      <div className="divide-y divide-slate-700">
                        {family.controls.map((control, ctrlIdx) => (
                          <div key={ctrlIdx} className="p-4">
                            <div className="flex items-start gap-3 mb-3">
                              <span className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs font-mono rounded">
                                {control.controlId}
                              </span>
                              <div className="flex-1">
                                <h4 className="text-white font-medium">{control.title}</h4>
                                <p className="text-slate-400 text-sm mt-1">{control.description}</p>
                              </div>
                            </div>

                            {/* Security Control Requirements (formerly MUST Statements) */}
                            <div className="ml-16 mb-3">
                              <h5 className="text-violet-400 text-sm font-semibold mb-2">Security Control Requirements</h5>
                              <ul className="space-y-1">
                                {control.mustStatements?.map((must, mustIdx) => {
                                  // Handle both string format (static data) and object format (user data)
                                  let statementText = typeof must === 'string' ? must : must.statement;
                                  const status = typeof must === 'object' ? must.status : 'MUST';
                                  const isDoes = status === 'DOES';
                                  
                                  // Remove "MUST" prefix from statement text (redundant since badge shows status)
                                  statementText = statementText.replace(/^MUST\s+/i, '');
                                  
                                  return (
                                    <li key={mustIdx} className="text-sm flex items-start gap-2">
                                      <span className={`px-1.5 py-0.5 rounded text-xs font-semibold ${
                                        isDoes 
                                          ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' 
                                          : 'bg-red-500/20 text-red-400 border border-red-500/30'
                                      }`}>
                                        {isDoes ? 'DOES' : 'MUST'}
                                      </span>
                                      <span className={isDoes ? 'text-emerald-300' : 'text-slate-300'}>
                                        {statementText}
                                      </span>
                                    </li>
                                  );
                                })}
                              </ul>
                            </div>

                            {/* Evidence Required */}
                            <div className="ml-16 mb-3">
                              <h5 className="text-blue-400 text-sm font-semibold mb-2">Evidence Required</h5>
                              <ul className="grid sm:grid-cols-2 gap-1">
                                {control.evidenceRequired?.map((evidence, evIdx) => (
                                  <li key={evIdx} className="text-slate-400 text-sm flex items-start gap-2">
                                    <span className="text-blue-400 mt-0.5">□</span>
                                    {evidence}
                                  </li>
                                ))}
                              </ul>
                            </div>

                            {/* HIPAA Mapping */}
                            {control.hipaaMapping?.length > 0 && (
                              <div className="ml-16">
                                <span className="text-violet-400 text-xs">HIPAA Mapping: {control.hipaaMapping.join(', ')}</span>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Evidence Checklist Summary */}
            <div className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-xl p-6" data-testid="evidence-checklist">
              <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                <FileCheck className="w-6 h-6 text-emerald-400" />
                Evidence Checklist Summary
              </h2>
              
              {/* Per-Question Evidence (from Assessment) */}
              {evidenceChecklist?.perQuestionEvidence?.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-medium text-white mb-3">Assessment Evidence Progress</h3>
                  <div className="grid md:grid-cols-4 gap-4 mb-4">
                    <div className="p-4 bg-slate-700/30 rounded-lg text-center">
                      <div className="text-3xl font-bold text-violet-400">{evidenceChecklist?.perQuestionSummary?.totalQuestions || 0}</div>
                      <p className="text-slate-400 text-sm">Questions with Evidence</p>
                    </div>
                    <div className="p-4 bg-slate-700/30 rounded-lg text-center">
                      <div className="text-3xl font-bold text-cyan-400">{evidenceChecklist?.perQuestionSummary?.totalItems || 0}</div>
                      <p className="text-slate-400 text-sm">Total Evidence Items</p>
                    </div>
                    <div className="p-4 bg-slate-700/30 rounded-lg text-center">
                      <div className="text-3xl font-bold text-emerald-400">{evidenceChecklist?.perQuestionSummary?.checkedItems || 0}</div>
                      <p className="text-slate-400 text-sm">Items Collected</p>
                    </div>
                    <div className="p-4 bg-slate-700/30 rounded-lg text-center">
                      <div className="text-3xl font-bold text-emerald-400">{evidenceChecklist?.perQuestionSummary?.completionRate || 0}%</div>
                      <p className="text-slate-400 text-sm">Completion Rate</p>
                    </div>
                  </div>
                  
                  {/* Per-Question Evidence List */}
                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {evidenceChecklist.perQuestionEvidence.map((question) => (
                      <div key={question.questionId} className="p-4 bg-slate-700/30 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-slate-400 font-mono text-sm">{question.questionId}</span>
                          <span className={`text-sm px-2 py-1 rounded ${
                            question.checkedItems === question.totalItems 
                              ? 'bg-emerald-500/20 text-emerald-400' 
                              : 'bg-amber-500/20 text-amber-400'
                          }`}>
                            {question.checkedItems}/{question.totalItems} collected
                          </span>
                        </div>
                        <p className="text-white text-sm mb-2">{question.questionText}</p>
                        <div className="space-y-1">
                          {question.items.map((item, idx) => (
                            <div key={idx} className="flex items-center gap-2 text-sm">
                              {item.checked ? (
                                <CheckCircle className="w-4 h-4 text-emerald-400" />
                              ) : (
                                <Clock className="w-4 h-4 text-slate-500" />
                              )}
                              <span className={item.checked ? 'text-emerald-300' : 'text-slate-400'}>
                                {item.text}
                              </span>
                              {item.isFromExcel && (
                                <span className="text-xs px-1 py-0.5 bg-cyan-500/20 text-cyan-400 rounded">Required</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <p className="text-slate-400 mb-6">
                {evidenceChecklist?.totalItems || 0} total evidence items required across both frameworks
              </p>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(evidenceChecklist?.categories || {}).map(([category, items]) => (
                  <div key={category} className="p-4 bg-slate-700/30 rounded-lg">
                    <h3 className="text-white font-medium mb-2">{category}</h3>
                    <div className="text-3xl font-bold text-emerald-400">{items.length}</div>
                    <p className="text-slate-400 text-sm">items</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Prioritization Tab - Sortable Security Controls Table */}
        {activeTab === 'prioritization' && prioritizationData && (
          <div className="space-y-6" data-testid="prioritization-tab">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
                <div className="text-slate-400 text-sm mb-1">Total Controls</div>
                <div className="text-2xl font-bold text-white">{prioritizationData.summary?.total || 0}</div>
              </div>
              <div className="bg-emerald-900/30 border border-emerald-500/30 rounded-xl p-4">
                <div className="text-emerald-400 text-sm mb-1">Compliant (Yes)</div>
                <div className="text-2xl font-bold text-emerald-400">{prioritizationData.summary?.yesCount || 0}</div>
              </div>
              <div className="bg-red-900/30 border border-red-500/30 rounded-xl p-4">
                <div className="text-red-400 text-sm mb-1">Non-Compliant (No)</div>
                <div className="text-2xl font-bold text-red-400">{prioritizationData.summary?.noCount || 0}</div>
              </div>
              <div className="bg-violet-900/30 border border-violet-500/30 rounded-xl p-4">
                <div className="text-violet-400 text-sm mb-1">Compliance Rate</div>
                <div className="text-2xl font-bold text-violet-400">{prioritizationData.summary?.complianceRate || 0}%</div>
              </div>
            </div>

            {/* Export Section */}
            <div className="flex items-center justify-between bg-slate-800/30 border border-slate-700/30 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <FileSpreadsheet className="w-5 h-5 text-emerald-400" />
                <div>
                  <h3 className="text-white font-medium">Export Prioritization Report</h3>
                  <p className="text-slate-400 text-sm">Download as Excel with Summary, All Controls, Compliant, and Non-Compliant sheets</p>
                </div>
              </div>
              <button
                onClick={exportPrioritizationAsExcel}
                className="flex items-center gap-2 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white font-medium rounded-lg transition-all shadow-lg shadow-emerald-900/30 hover:shadow-emerald-900/50"
                data-testid="export-excel-btn"
              >
                <Download className="w-4 h-4" />
                Export Excel
              </button>
            </div>

            {/* Sort Controls */}
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="text-slate-400 text-sm font-medium">Sort By:</span>
                <button
                  onClick={async () => {
                    const newSort = { field: 'sort', ascending: prioritizationSort.field === 'sort' ? !prioritizationSort.ascending : true };
                    setPrioritizationSort(newSort);
                    try {
                      const res = await fetch(`${API_URL}/api/ssp-report/keystone/prioritization?sort_by=${newSort.field}&ascending=${newSort.ascending}`);
                      const data = await res.json();
                      setPrioritizationData(data);
                    } catch (e) { console.error('Sort error:', e); }
                  }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                    prioritizationSort.field === 'sort' 
                      ? 'bg-violet-600 text-white' 
                      : 'bg-slate-700/50 text-slate-300 hover:bg-slate-600/50'
                  }`}
                  data-testid="sort-by-order"
                >
                  <ListOrdered className="w-4 h-4" />
                  Order (Column G)
                  {prioritizationSort.field === 'sort' && (
                    prioritizationSort.ascending ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                  )}
                </button>
                <button
                  onClick={async () => {
                    const newSort = { field: 'priority', ascending: prioritizationSort.field === 'priority' ? !prioritizationSort.ascending : true };
                    setPrioritizationSort(newSort);
                    try {
                      const res = await fetch(`${API_URL}/api/ssp-report/keystone/prioritization?sort_by=${newSort.field}&ascending=${newSort.ascending}`);
                      const data = await res.json();
                      setPrioritizationData(data);
                    } catch (e) { console.error('Sort error:', e); }
                  }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                    prioritizationSort.field === 'priority' 
                      ? 'bg-violet-600 text-white' 
                      : 'bg-slate-700/50 text-slate-300 hover:bg-slate-600/50'
                  }`}
                  data-testid="sort-by-priority"
                >
                  <ArrowUpDown className="w-4 h-4" />
                  Priority (Column E)
                  {prioritizationSort.field === 'priority' && (
                    prioritizationSort.ascending ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                  )}
                </button>
              </div>
              
              {/* Filter Controls */}
              <div className="flex flex-wrap items-center gap-4 pt-4 border-t border-slate-700/50">
                <span className="text-slate-400 text-sm font-medium flex items-center gap-2">
                  <Filter className="w-4 h-4" /> Filter:
                </span>
                
                {/* Control Family Filter */}
                <select
                  value={prioritizationFilters.family}
                  onChange={(e) => setPrioritizationFilters(prev => ({ ...prev, family: e.target.value }))}
                  className="px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                  data-testid="filter-by-family"
                >
                  <option value="all">All Control Families</option>
                  <option value="Access Control">Access Control</option>
                  <option value="Account Management">Account Management</option>
                  <option value="Identification and Authentication">Identification & Authentication</option>
                  <option value="Audit and Accountability">Audit & Accountability</option>
                  <option value="Awareness and Training">Awareness & Training</option>
                  <option value="Incident Response">Incident Response</option>
                  <option value="Continuity">Continuity</option>
                  <option value="Risk Assessment">Risk Assessment</option>
                  <option value="System and Communications Protection">System & Communications Protection</option>
                  <option value="System and Information Integrity">System & Information Integrity</option>
                  <option value="Physical Access">Physical Access</option>
                  <option value="Physical Security">Physical Security</option>
                  <option value="Information Protection">Information Protection</option>
                  <option value="Program Management">Program Management</option>
                  <option value="Policies and Procedures General">Policies & Procedures</option>
                  <option value="System and Services Acquisition">System & Services Acquisition</option>
                </select>
                
                {/* Status Filter */}
                <select
                  value={prioritizationFilters.status}
                  onChange={(e) => setPrioritizationFilters(prev => ({ ...prev, status: e.target.value }))}
                  className="px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                  data-testid="filter-by-status"
                >
                  <option value="all">All Statuses</option>
                  <option value="Yes">Compliant (Yes)</option>
                  <option value="No">Non-Compliant (No)</option>
                </select>
                
                {/* Clear Filters */}
                {(prioritizationFilters.family !== 'all' || prioritizationFilters.status !== 'all') && (
                  <button
                    onClick={() => setPrioritizationFilters({ family: 'all', status: 'all' })}
                    className="flex items-center gap-1 px-3 py-2 text-sm text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                    data-testid="clear-filters"
                  >
                    <X className="w-4 h-4" /> Clear Filters
                  </button>
                )}
              </div>
            </div>

            {/* Prioritization Table */}
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden" data-testid="prioritization-table">
              <div className="p-4 border-b border-slate-700/50 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                  <ListOrdered className="w-5 h-5 text-violet-400" />
                  Security Control Prioritization
                </h2>
                <span className="text-sm text-slate-400">
                  Sorted by {prioritizationSort.field === 'sort' ? 'Order' : 'Priority'} ({prioritizationSort.ascending ? 'Ascending' : 'Descending'})
                </span>
              </div>
              <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
                <table className="w-full">
                  <thead className="bg-slate-900/50 sticky top-0">
                    <tr>
                      <th className="text-left p-4 text-slate-400 font-medium w-12">#</th>
                      <th className="text-left p-4 text-slate-400 font-medium w-48">Control</th>
                      <th className="text-left p-4 text-slate-400 font-medium">Definition</th>
                      <th className="text-center p-4 text-slate-400 font-medium w-24">Status</th>
                      <th className="text-center p-4 text-slate-400 font-medium w-20">Priority</th>
                      <th className="text-center p-4 text-slate-400 font-medium w-20">Order</th>
                    </tr>
                  </thead>
                  <tbody>
                    {prioritizationData.data
                      ?.filter(item => {
                        // Apply family filter
                        if (prioritizationFilters.family !== 'all') {
                          const itemFamily = item.secCntrl.replace(/ # \d+$/, '');
                          if (itemFamily !== prioritizationFilters.family) return false;
                        }
                        // Apply status filter
                        if (prioritizationFilters.status !== 'all') {
                          if (item.yesNo !== prioritizationFilters.status) return false;
                        }
                        return true;
                      })
                      .map((item, idx) => (
                      <tr 
                        key={idx} 
                        className={`border-t border-slate-700/30 hover:bg-slate-700/20 transition-colors ${
                          item.yesNo === 'Yes' ? 'bg-emerald-900/10' : 'bg-red-900/10'
                        }`}
                      >
                        <td className="p-4 text-slate-500 text-sm">{idx + 1}</td>
                        <td className="p-4">
                          <span className="text-white font-medium text-sm">{item.secCntrl}</span>
                        </td>
                        <td className="p-4 text-slate-300 text-sm leading-relaxed">{item.definition}</td>
                        <td className="p-4 text-center">
                          <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                            item.yesNo === 'Yes' 
                              ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' 
                              : 'bg-red-500/20 text-red-400 border border-red-500/30'
                          }`}>
                            {item.yesNo}
                          </span>
                        </td>
                        <td className="p-4 text-center">
                          <span className={`px-2 py-1 rounded text-xs font-mono ${
                            item.priority <= 50 ? 'bg-red-500/20 text-red-400' :
                            item.priority <= 100 ? 'bg-amber-500/20 text-amber-400' :
                            'bg-slate-500/20 text-slate-400'
                          }`}>
                            {item.priority}
                          </span>
                        </td>
                        <td className="p-4 text-center text-slate-400 font-mono text-sm">{item.sort}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'priorities' && (
          <div className="space-y-6">
            {/* Implementation Progress */}
            <div className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-xl p-6" data-testid="implementation-progress">
              <h2 className="text-lg font-semibold text-white mb-4">Implementation Progress</h2>
              <div className="flex items-center gap-6">
                <div className="w-32 h-32">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadialBarChart cx="50%" cy="50%" innerRadius="60%" outerRadius="100%" data={progressData}>
                      <RadialBar
                        minAngle={15}
                        background={{ fill: '#334155' }}
                        clockWise
                        dataKey="value"
                        cornerRadius={10}
                      />
                    </RadialBarChart>
                  </ResponsiveContainer>
                </div>
                <div>
                  <div className="text-4xl font-bold text-violet-400">{summary?.implementation_rate || 0}%</div>
                  <div className="text-slate-400">Overall Implementation</div>
                </div>
              </div>
            </div>

            {/* Priority Action Items */}
            <div className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-xl p-6" data-testid="priority-items">
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-400" />
                Priority Action Items
              </h2>
              <div className="space-y-3">
                {summary?.priority_areas?.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between p-4 bg-slate-700/30 rounded-lg border-l-4 border-l-red-500">
                    <div>
                      <h4 className="text-white font-medium">{item.area}</h4>
                      <span className={`text-sm ${
                        item.status.toLowerCase().includes('progress') ? 'text-blue-400' : 'text-slate-400'
                      }`}>
                        {item.status}
                      </span>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      item.priority === 'High' 
                        ? 'bg-red-500/20 text-red-400' 
                        : 'bg-amber-500/20 text-amber-400'
                    }`}>
                      {item.priority}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Recommendations */}
            <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-xl p-6" data-testid="recommendations">
              <h2 className="text-lg font-semibold text-amber-400 mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Recommendations
              </h2>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-amber-500/30 rounded-full flex items-center justify-center text-amber-400 text-sm font-bold">1</span>
                  <p className="text-slate-200">Prioritize Access Control Policy formalization and enforcement across all systems</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-amber-500/30 rounded-full flex items-center justify-center text-amber-400 text-sm font-bold">2</span>
                  <p className="text-slate-200">Implement centralized SIEM for comprehensive audit logging and real-time threat detection</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-amber-500/30 rounded-full flex items-center justify-center text-amber-400 text-sm font-bold">3</span>
                  <p className="text-slate-200">Complete MFA rollout across all privileged accounts and ePHI access points</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="mt-16 py-6 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-slate-500 text-sm">
            Prepared by Colt Cybersecurity Consultants in partnership with Aspiritech • {sspData?.organization?.date}
          </p>
          <p className="text-violet-400 text-sm mt-1 font-medium">
            Masks Down. Shields Up.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default SspReport;
